import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../store/store";
import { fetchAdDetailsById } from "../../features/adDetails/adDetailsThunks";
import { resetAdDetailsState } from "../../features/adDetails/adDetailsSlice";
import { fetchCurrentUser } from "../../features/auth/authThunks";
import PhoneInput from "../../components/PhoneInputComponent/PhoneInput";
import { toast } from "react-toastify";
import { cancelBookingByDates, saveBooking, setBookingDatesBlockedByOwner, updateBookingPriceByDate } from "../../features/booking/bookingThunks";
import styles from "./AdDetails.module.css";
import { useTranslation } from "react-i18next";
import { getLocalizedText } from "../../features/ads/i18nFields";
import { countryCodeConverterIso3ToIso2 } from "../../utils/countryCodeConverter";
import loaderStyles from "../../components/NavigationLoader/NavigationLoader.module.css";
import SimpleCaptcha, { createCaptchaChallenge, type CaptchaChallenge } from "../../components/SimpleCaptcha/SimpleCaptcha";
import { deleteAdCascadeApi, getPaginatedAdsApi, updateAdStatusApi } from "../../api/adsApi";
import { registerApi } from "../../api/registerApi";
import { listAdCommentsApi, saveAdCommentApi, type AdCommentDto } from "../../api/commentApi";
import { listApprovedRateAdsByAdApi, type RateAdDto } from "../../api/rateAdApi";
import { reportOwnerByAdApi } from "../../api/reportApi";
import { getCaptchaChallengeApi } from "../../api/captchaApi";
import type { Ad } from "../../features/ads/adsTypes";
import ShimmerImage from "../../components/ShimmerImage/ShimmerImage";
import { hasLeadingZeroPhoneDigit } from "../../utils/phoneValidation";
import { AD_IMAGE_PLACEHOLDER } from "../../utils/adImagePlaceholder";
import { getCountryValueDisplay, type CountryValue } from "../../features/country/countrySlice";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { FiHome } from "react-icons/fi";
import {
  MdOutlineAcUnit,
  MdOutlineChair,
  MdOutlineElevator,
  MdOutlineFireplace,
  MdOutlineLandscape,
  MdOutlineLocalFireDepartment,
  MdOutlineWaves,
} from "react-icons/md";
const placeholder = AD_IMAGE_PLACEHOLDER;

const AirConditionerIcon = MdOutlineAcUnit as unknown as React.ComponentType<any>;
const HeaterIcon = MdOutlineFireplace as unknown as React.ComponentType<any>;
const BoilerIcon = MdOutlineLocalFireDepartment as unknown as React.ComponentType<any>;
const ElevatorIcon = MdOutlineElevator as unknown as React.ComponentType<any>;
const FurnishedHomeIcon = MdOutlineChair as unknown as React.ComponentType<any>;
const HomeIcon = FiHome as unknown as React.ComponentType<any>;
const PanoramaIcon = MdOutlineLandscape as unknown as React.ComponentType<any>;
const SeaViewIcon = MdOutlineWaves as unknown as React.ComponentType<any>;
const DEFAULT_LEAFLET_ICON = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const API_LOADER_TIMEOUT_MS = 25000;

const PARTICULARITY_ICONS = {
  climatiseur: AirConditionerIcon,
  chauffage: HeaterIcon,
  chaudiere: BoilerIcon,
  ascenseur: ElevatorIcon,
  logement_equipe: FurnishedHomeIcon,
  vue_panoramique: PanoramaIcon,
  vue_sur_mer: SeaViewIcon,
} as const;

type ParticularityIconKey = keyof typeof PARTICULARITY_ICONS;

const PARTICULARITY_VALUE_TO_KEY = {
  climatiseur: "climatiseur",
  "air conditioner": "climatiseur",
  chauffage: "chauffage",
  heating: "chauffage",
  chaudiere: "chaudiere",
  chaudière: "chaudiere",
  boiler: "chaudiere",
  ascenseur: "ascenseur",
  elevator: "ascenseur",
  "logement equipe": "logement_equipe",
  "logement équipé": "logement_equipe",
  "fully equipped home": "logement_equipe",
  "vue panoramique": "vue_panoramique",
  "panoramic view": "vue_panoramique",
  "vue sur mer": "vue_sur_mer",
  "sea view": "vue_sur_mer",
} as const;

const normalizeParticularityKey = (value: string): ParticularityIconKey | null => {
  const normalized = value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return (PARTICULARITY_VALUE_TO_KEY[normalized as keyof typeof PARTICULARITY_VALUE_TO_KEY] ??
    null) as ParticularityIconKey | null;
};

type CalendarCell = {
  date: Date;
  inCurrentMonth: boolean;
};

interface CalendarSignupFormData {
  email: string;
  password: string;
  confirme_password: string;
  firstName: string;
  lastName: string;
  phone: string;
  codeCountry: string;
  indicatif: string;
  status: string;
  nbr_child: number | "";
  country: string;
}

interface CalendarSignupErrors {
  email?: string;
  password?: string;
  confirme_password?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: string;
  nbr_child?: string;
  captcha?: string;
}

type ReservationUserName = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  nbr_child: number | null;
  country: CountryValue;
  indicatif: string;
  codeCountry: string;
  reservedDates: Array<{
    dateKey: string;
    booked: boolean;
  }>;
  hasPendingDates: boolean;
  hasConfirmedDates: boolean;
};

const normalizeDate = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addMonths = (value: Date, months: number): Date =>
  new Date(value.getFullYear(), value.getMonth() + months, 1);

const isSameDay = (first: Date, second: Date): boolean =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

const buildMonthCells = (monthDate: Date): CalendarCell[] => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPreviousMonth = new Date(year, month, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let i = firstDayIndex - 1; i >= 0; i -= 1) {
    cells.push({
      date: new Date(year, month - 1, daysInPreviousMonth - i),
      inCurrentMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(year, month, day),
      inCurrentMonth: true,
    });
  }

  let trailingDay = 1;
  while (cells.length < 42) {
    cells.push({
      date: new Date(year, month + 1, trailingDay),
      inCurrentMonth: false,
    });
    trailingDay += 1;
  }

  return cells;
};

const formatLongDate = (value: Date | null, locale: string, fallbackLabel: string): string => {
  if (!value) return fallbackLabel;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(value);
};

const formatFullDateFromKey = (dateKey: string, locale: string): string => {
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateKey;
  const label = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatMonthTitle = (value: Date, locale: string): string => {
  const label = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(value);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatDateForApi = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDaysToDateKey = (dateKey: string, amount: number): string | null => {
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setDate(parsed.getDate() + amount);
  return formatDateForApi(parsed);
};

const formatPhoneByPairs = (rawValue: string): string => {
  const value = String(rawValue || "").trim();
  if (!value) return "";
  const hasPlus = value.startsWith("+");
  const digits = value.replace(/\D/g, "");
  if (!digits) return value;
  const groups = digits.match(/.{1,2}/g) || [];
  return `${hasPlus ? "+" : ""}${groups.join(" ")}`;
};

const formatPhoneForDisplay = (rawValue: string): string => {
  const value = String(rawValue || "").trim();
  if (!value) return "";
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) return parsed.formatInternational();
  const asYouTypeValue = new AsYouType().input(value);
  return asYouTypeValue || formatPhoneByPairs(value);
};

const normalizeAddressSegment = (value: string): string =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();

const cleanRepeatedAddressSegments = (value: string): string => {
  const rawAddress = String(value || "").trim();
  if (!rawAddress) return "";

  const seenSegments = new Set<string>();
  const cleanedSegments: string[] = [];
  let hasReachedCountry = false;

  rawAddress
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .forEach((segment) => {
      const normalizedSegment = normalizeAddressSegment(segment);
      if (!normalizedSegment) return;
      if (hasReachedCountry) return;
      if (seenSegments.has(normalizedSegment)) return;

      cleanedSegments.push(segment);
      seenSegments.add(normalizedSegment);

      if (normalizedSegment === "algerie" || normalizedSegment === "algeria") {
        hasReachedCountry = true;
      }
    });

  return cleanedSegments.join(", ");
};

const getCommentDateLabel = (createdAt: string | undefined, locale: string): string => {
  if (!createdAt) return "";
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return "";

  const today = new Date();
  const todayKey = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const targetKey = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  const dayDiff = Math.round((todayKey - targetKey) / 86400000);

  if (dayDiff === 1) return "Hier";

  const formattedDate = new Intl.DateTimeFormat(locale || "fr", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
  return `Le ${formattedDate}`;
};

const splitCommentContent = (content?: string): { title: string; body: string } => {
  const raw = typeof content === "string" ? content.trim() : "";
  if (!raw) return { title: "", body: "" };
  const sentenceMatch = raw.match(/^(.{18,130}?[.!?])(\s+|$)/);
  if (sentenceMatch) {
    const title = capitalizeCommentStart(sentenceMatch[1]);
    const body = capitalizeCommentStart(raw.slice(sentenceMatch[0].length));
    return { title, body };
  }
  if (raw.length <= 130) return { title: capitalizeCommentStart(raw), body: "" };
  return {
    title: capitalizeCommentStart(`${raw.slice(0, 127).trim()}...`),
    body: capitalizeCommentStart(raw.slice(127).trim()),
  };
};

const capitalizeCommentStart = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const capitalizeFirstLetter = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const firstLetterMatch = trimmed.match(/\p{L}/u);
  const firstLetterIndex = firstLetterMatch?.index ?? -1;
  if (firstLetterIndex < 0) return trimmed;
  return `${trimmed.slice(0, firstLetterIndex)}${trimmed.charAt(firstLetterIndex).toUpperCase()}${trimmed.slice(firstLetterIndex + 1)}`;
};

const DESCRIPTION_LIST_MARKER_SOURCE = String.raw`[\p{Emoji_Presentation}\p{Extended_Pictographic}]\ufe0f?(?:\p{Emoji_Modifier})?(?:\u200d[\p{Emoji_Presentation}\p{Extended_Pictographic}]\ufe0f?(?:\p{Emoji_Modifier})?)*`;
const DESCRIPTION_LIST_MARKER_REGEX = new RegExp(`${DESCRIPTION_LIST_MARKER_SOURCE}\\s*`, "u");
const DESCRIPTION_LIST_LEADING_MARKER_REGEX = new RegExp(`^\\s*(?:${DESCRIPTION_LIST_MARKER_SOURCE}\\s*)+`, "u");
const DESCRIPTION_LIST_MARKER_ONLY_REGEX = new RegExp(`^(?:${DESCRIPTION_LIST_MARKER_SOURCE}\\s*)+$`, "u");

const splitContinuousIconDescription = (value: string): { intro: string; items: string[] } => {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return { intro: "", items: [] };

  const markerMatches = Array.from(normalized.matchAll(new RegExp(DESCRIPTION_LIST_MARKER_REGEX.source, "gu")));
  if (markerMatches.length === 0) return { intro: normalized, items: [] };

  const firstMarkerIndex = markerMatches[0].index ?? -1;
  if (firstMarkerIndex < 0) return { intro: normalized, items: [] };

  const intro = normalized.slice(0, firstMarkerIndex).trim();
  const rawList = normalized.slice(firstMarkerIndex).trim();
  const splitEntries = rawList
    .split(new RegExp(`(?=${DESCRIPTION_LIST_MARKER_SOURCE})`, "gu"))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length >= 2);

  const items: string[] = [];
  let pendingIcons = "";
  splitEntries.forEach((entry) => {
    if (DESCRIPTION_LIST_MARKER_ONLY_REGEX.test(entry)) {
      pendingIcons = [pendingIcons, entry].filter(Boolean).join(" ");
      return;
    }

    items.push([pendingIcons, entry].filter(Boolean).join(" ").trim());
    pendingIcons = "";
  });

  return items.length > 0 ? { intro, items } : { intro: normalized, items: [] };
};

const splitSmartDescription = (value: string): { intro: string; items: string[] } => {
  const raw = String(value || "").trim();
  if (!raw) return { intro: "", items: [] };

  const lines = raw
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .filter(Boolean);

  const firstListLineIndex = lines.findIndex((line) => (
    DESCRIPTION_LIST_LEADING_MARKER_REGEX.test(line) ||
    DESCRIPTION_LIST_MARKER_ONLY_REGEX.test(line)
  ));

  if (firstListLineIndex < 0) {
    return splitContinuousIconDescription(raw);
  }

  const intro = lines.slice(0, firstListLineIndex).join(" ").trim();
  const listLines = lines.slice(firstListLineIndex);

  const items: string[] = [];
  let pendingIcons = "";
  listLines.forEach((line) => {
    if (DESCRIPTION_LIST_MARKER_ONLY_REGEX.test(line)) {
      pendingIcons = [pendingIcons, line].filter(Boolean).join(" ");
      return;
    }

    if (DESCRIPTION_LIST_LEADING_MARKER_REGEX.test(line) || pendingIcons) {
      items.push([pendingIcons, line].filter(Boolean).join(" ").trim());
      pendingIcons = "";
      return;
    }

    if (items.length > 0) {
      items[items.length - 1] = `${items[items.length - 1]} ${line}`.trim();
    }
  });

  return items.length > 0 ? { intro, items } : splitContinuousIconDescription(raw);
};

const getMsUntilNextLocalMidnight = (): number => {
  const now = new Date();
  const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1, 0);
  return Math.max(1000, nextMidnight.getTime() - now.getTime());
};

const getDateRange = (start: Date, end: Date): string[] => {
  const range: string[] = [];
  const current = normalizeDate(start);
  const normalizedEnd = normalizeDate(end);

  while (current.getTime() <= normalizedEnd.getTime()) {
    range.push(formatDateForApi(current));
    current.setDate(current.getDate() + 1);
  }

  return range;
};

const toDateKey = (value: unknown): string | null => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDateForApi(normalizeDate(value));
  }

  if (typeof value === "object") {
    const dateLike =
      (value as any)?.date ??
      (value as any)?.bookingDate ??
      (value as any)?.day ??
      null;
    if (dateLike) {
      return toDateKey(dateLike);
    }
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const prefixedDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (prefixedDate) return prefixedDate[1];

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return formatDateForApi(normalizeDate(parsed));
    }
  }

  return null;
};

const toBookedFlag = (value: unknown): boolean | null => {
  if (!value || typeof value !== "object") return null;
  if ("booked" in (value as Record<string, unknown>)) {
    const rawBooked = (value as { booked?: unknown }).booked;
    if (rawBooked === null) return null;
    if (typeof rawBooked === "boolean") return rawBooked;
  }
  return null;
};

const normalizeUserStatus = (value: string): "single" | "married" | "married_with_children" | "" => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "single" || normalized === "celibataire" || normalized === "célibataire") return "single";
  if (normalized === "married" || normalized === "marie_sans_enfants" || normalized === "marié") return "married";
  if (
    normalized === "married_with_children" ||
    normalized === "marie_avec_enfants" ||
    normalized === "marié avec enfants"
  ) {
    return "married_with_children";
  }
  return "";
};

const sortPriorityPhotoFirst = (photos: any[]) => {
  const priorityIndex = photos.findIndex((photo) => photo && typeof photo === "object" && photo.prioritaire === true);
  if (priorityIndex <= 0) return photos;
  const nextPhotos = [...photos];
  const [priorityPhoto] = nextPhotos.splice(priorityIndex, 1);
  return [priorityPhoto, ...nextPhotos];
};

const normalizeAdsListItem = (payload: any): Ad => {
  const raw = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const normalizedPhotos = Array.isArray(raw?.photos)
    ? sortPriorityPhotoFirst(raw.photos)
        .map((photo: any) =>
          typeof photo === "string"
            ? photo
            : photo?.url || photo?.name || photo?.path || photo?.src
        )
        .filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  } as Ad;
};

const AdDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || "fr";
  const isArabicLocale = locale.toLowerCase().startsWith("ar");
  const weekDays = t("adDetails.weekDays", { returnObjects: true }) as string[];
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { item, loading, error } = useSelector((state: RootState) => state.adDetails);
  const { user, bookingStreamTick } = useSelector((state: RootState) => state.auth);
  const currentUserId = user?.id || user?._id || null;
  const currentUserRole = user?.role || "";
  const [activePhoto, setActivePhoto] = useState(0);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isOwnerPhonePopupOpen, setIsOwnerPhonePopupOpen] = useState(false);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(0);
  const [viewerDragOffsetX, setViewerDragOffsetX] = useState(0);
  const [viewerDragTransitionMs, setViewerDragTransitionMs] = useState(0);
  const [viewerDragTransitionEasing, setViewerDragTransitionEasing] = useState("ease-out");
  const [viewerTrackOpacity, setViewerTrackOpacity] = useState(1);
  const [viewerTrackOpacityTransitionMs, setViewerTrackOpacityTransitionMs] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMobileCalendar, setIsMobileCalendar] = useState(false);
  const [hasViewedSecondCalendarMonth, setHasViewedSecondCalendarMonth] = useState(false);
  const secondCalendarMonthRef = useRef<HTMLDivElement | null>(null);
  const calendarSignupRef = useRef<HTMLElement | null>(null);
  const calendarScrollRef = useRef<HTMLDivElement | null>(null);
  const calendarFooterRef = useRef<HTMLDivElement | null>(null);
  const [isCalendarFooterFloating, setIsCalendarFooterFloating] = useState(false);
  const [arrivalDate, setArrivalDate] = useState<Date | null>(null);
  const [departureDate, setDepartureDate] = useState<Date | null>(null);
  const [isPricePopupOpen, setIsPricePopupOpen] = useState(false);
  const [pricePopupView, setPricePopupView] = useState<"menu" | "priceForm" | "reservationsList" | "ownerSignup" | "ownerSignupSuccess">("menu");
  const [pricePopupValue, setPricePopupValue] = useState("");
  const [datePriceOverrides, setDatePriceOverrides] = useState<Record<string, number>>({});
  const [selectedPriceDate, setSelectedPriceDate] = useState<Date | null>(null);
  const [selectedOwnerActionDates, setSelectedOwnerActionDates] = useState<Set<string>>(new Set());
  const [selectedOwnerActionCategory, setSelectedOwnerActionCategory] = useState<string | null>(null);
  const [calendarDateError, setCalendarDateError] = useState("");
  const [isMinimumNightsPopupOpen, setIsMinimumNightsPopupOpen] = useState(false);
  const [isColorLegendPopupOpen, setIsColorLegendPopupOpen] = useState(false);
  const [isBlockAdConfirmOpen, setIsBlockAdConfirmOpen] = useState(false);
  const [isBlockingAd, setIsBlockingAd] = useState(false);
  const [isValidateAdConfirmOpen, setIsValidateAdConfirmOpen] = useState(false);
  const [isValidatingAd, setIsValidatingAd] = useState(false);
  const [isDeleteAdConfirmOpen, setIsDeleteAdConfirmOpen] = useState(false);
  const [isDeletingAd, setIsDeletingAd] = useState(false);
  const [selectedPendingCancelDates, setSelectedPendingCancelDates] = useState<Set<string>>(new Set());
  const [localPendingBookingDates, setLocalPendingBookingDates] = useState<Set<string>>(new Set());
  const [expandedReservationUserId, setExpandedReservationUserId] = useState<string | null>(null);
  const [isBookingSubmitting, setIsBookingSubmitting] = useState(false);
  const [apiPendingCount, setApiPendingCount] = useState(0);
  const [signupPhoneMaskDigits, setSignupPhoneMaskDigits] = useState<number | null>(null);
  const [signupErrors, setSignupErrors] = useState<CalendarSignupErrors>({});
  const [signupCaptchaChallenge, setSignupCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [signupCaptchaInput, setSignupCaptchaInput] = useState("");
  const [relatedAds, setRelatedAds] = useState<Ad[]>([]);
  const [comments, setComments] = useState<AdCommentDto[]>([]);
  const [stayReviews, setStayReviews] = useState<RateAdDto[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentModerationPopupOpen, setCommentModerationPopupOpen] = useState(false);
  const [ownerReportModalOpen, setOwnerReportModalOpen] = useState(false);
  const [ownerReportMessage, setOwnerReportMessage] = useState("");
  const [ownerReportSubmitting, setOwnerReportSubmitting] = useState(false);
  const [commentRating, setCommentRating] = useState(1);
  const [commentHoverRating, setCommentHoverRating] = useState<number | null>(null);
  const [commentContent, setCommentContent] = useState("");
  const [commentCaptchaChallenge, setCommentCaptchaChallenge] = useState<CaptchaChallenge>(createCaptchaChallenge);
  const [commentCaptchaInput, setCommentCaptchaInput] = useState("");
  const [commentCaptchaError, setCommentCaptchaError] = useState("");
  const [commentCaptchaPopupOpen, setCommentCaptchaPopupOpen] = useState(false);
  const viewerTouchStartXRef = useRef<number | null>(null);
  const viewerTouchCurrentXRef = useRef<number | null>(null);
  const viewerTouchStartYRef = useRef<number | null>(null);
  const viewerTouchStartTimeRef = useRef<number | null>(null);
  const viewerTouchLockAxisRef = useRef<"x" | "y" | null>(null);
  const viewerSwipeTimerRef = useRef<number | null>(null);
  const viewerFadeTimerRef = useRef<number | null>(null);
  const viewerPreloadedPhotosRef = useRef<Set<string>>(new Set());
  const viewerViewportRef = useRef<HTMLDivElement | null>(null);
  const viewerViewportWidthRef = useRef<number>(0);
  const viewerIsAnimatingRef = useRef(false);
  const [viewerViewportWidth, setViewerViewportWidth] = useState<number>(() =>
    typeof window !== "undefined" ? Math.max(1, window.innerWidth || 360) : 360
  );
  const [signupData, setSignupData] = useState<CalendarSignupFormData>({
    email: "",
    password: "",
    confirme_password: "",
    firstName: "",
    lastName: "",
    phone: "",
    codeCountry: "",
    indicatif: "",
    status: "",
    nbr_child: "",
    country: "",
  });
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [today, setToday] = useState(() => normalizeDate(new Date()));
  const photoCount =
    Array.isArray(item?.photos) && (item?.photos?.length ?? 0) > 0
      ? (item?.photos?.length ?? 1)
      : 1;
  const commentsDisabled = Boolean(item?.desactiver_commentaire);
  const minimumNights = Math.min(5, Math.max(1, Number((item as any)?.min_nights) || 1));
  const statusLabelByValue = useMemo<Record<string, string>>(() => ({
    single: t("adDetails.statusSingle"),
    married: t("adDetails.statusMarriedNoChildren"),
    married_with_children: t("adDetails.statusMarriedWithChildren"),
  }), [t]);
  const runWithApiLoader = async <T,>(action: () => Promise<T>): Promise<T> => {
    setApiPendingCount((current) => current + 1);
    let timeoutId: number | undefined;
    try {
      return await Promise.race([
        action(),
        new Promise<never>((_resolve, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error(t("system.requestTimeout", { defaultValue: "La requete a pris trop de temps." })));
          }, API_LOADER_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      setApiPendingCount((current) => Math.max(0, current - 1));
    }
  };
  const refreshSignupCaptcha = useCallback(async () => {
    setSignupCaptchaInput("");
    try {
      const challenge = await getCaptchaChallengeApi();
      setSignupCaptchaChallenge(challenge);
    } catch {
      setSignupCaptchaChallenge(createCaptchaChallenge());
      setSignupErrors((prev) => ({ ...prev, captcha: t("system.serverError") }));
    }
  }, [t]);

  const refreshCommentCaptcha = useCallback(async (errorMessage = "") => {
    setCommentCaptchaInput("");
    setCommentCaptchaError(errorMessage);
    try {
      const challenge = await getCaptchaChallengeApi();
      setCommentCaptchaChallenge(challenge);
    } catch {
      setCommentCaptchaChallenge(createCaptchaChallenge());
      setCommentCaptchaError(errorMessage || t("system.serverError"));
    }
  }, [t]);

  useEffect(() => {
    if (user) return;
    void refreshSignupCaptcha();
  }, [refreshSignupCaptcha, user]);

  const getLocalizedCountryLabel = (countryCodeRaw: string, fallbackCountry: CountryValue) => {
    const fallbackLabel = getCountryValueDisplay(fallbackCountry, "", locale);
    const raw = String(countryCodeRaw || "").trim().toUpperCase();
    let iso2 = raw;
    if (raw.length === 3 && countryCodeConverterIso3ToIso2.has(raw)) {
      iso2 = String(countryCodeConverterIso3ToIso2.get(raw) || "").toUpperCase();
    }
    if (iso2.length === 2) {
      try {
        const names = new Intl.DisplayNames([locale], { type: "region" });
        const localized = names.of(iso2);
        if (localized) return localized;
      } catch (_err) {
        // no-op
      }
    }
    return fallbackLabel || "";
  };

  useEffect(() => {
    dispatch(resetAdDetailsState());
    if (!id) return;
    const request = dispatch(fetchAdDetailsById(id));
    return () => { request.abort(); };
  }, [dispatch, id, currentUserId, currentUserRole]);

  useEffect(() => {
    if (!id || bookingStreamTick === 0) return;
    void dispatch(fetchAdDetailsById(id));
  }, [bookingStreamTick, dispatch, id]);

  useEffect(() => {
    let cancelled = false;
    const loadRelatedAds = async () => {
      try {
        const response = await getPaginatedAdsApi({ limit: 16, status: true });
        const rawList = Array.isArray(response?.data?.data) ? response.data.data : [];
        const normalizedList = rawList
          .map((entry: any) => normalizeAdsListItem(entry))
          .filter((entry) => {
            const adId = entry?.id || (entry as any)?._id;
            return adId && String(adId) !== String(id);
          });
        if (!cancelled) setRelatedAds(normalizedList);
      } catch (_error) {
        if (!cancelled) setRelatedAds([]);
      }
    };
    void loadRelatedAds();

    return () => {
      cancelled = true;
    };
  }, [id, locale]);

  useEffect(() => {
    if (!id) return;
    if (commentsDisabled) {
      setComments([]);
      setCommentsLoading(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setCommentsLoading(true);
      try {
        const response = await listAdCommentsApi(id);
        if (cancelled) return;
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];
        setComments(data);
      } catch (_error) {
        if (!cancelled) {
          setComments([]);
        }
      } finally {
        if (!cancelled) setCommentsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [commentsDisabled, id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const run = async () => {
      try {
        const response = await listApprovedRateAdsByAdApi(id);
        if (!cancelled) {
          setStayReviews(Array.isArray(response?.data?.data) ? response.data.data : []);
        }
      } catch (_error) {
        if (!cancelled) setStayReviews([]);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let timeoutId: number | undefined;
    let cancelled = false;

    const scheduleNextRefresh = () => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setToday(normalizeDate(new Date()));
        void (async () => {
          try {
            await dispatch(fetchCurrentUser());
            if (id) {
              await dispatch(fetchAdDetailsById(id));
            }
          } catch (_error) {
            // Background refresh failures should not block the page.
          }
        })();
        scheduleNextRefresh();
      }, getMsUntilNextLocalMidnight());
    };

    scheduleNextRefresh();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [dispatch, id]);

  useEffect(() => {
    setActivePhoto(0);
  }, [item?.id]);

  useEffect(() => {
    if (!isImageViewerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsImageViewerOpen(false);
        return;
      }
      if (event.key === "ArrowRight") {
        setViewerPhotoIndex((prev) => (prev + 1) % photoCount);
        return;
      }
      if (event.key === "ArrowLeft") {
        setViewerPhotoIndex((prev) => (prev - 1 + photoCount) % photoCount);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isImageViewerOpen, photoCount]);

  useEffect(() => {
    if (!isImageViewerOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      if (!isCalendarOpen) {
        document.body.style.overflow = "";
      }
    };
  }, [isImageViewerOpen, isCalendarOpen]);

  const syncViewerViewportWidth = useCallback(() => {
    const measured =
      viewerViewportRef.current?.clientWidth ||
      (typeof window !== "undefined" ? window.innerWidth : 360) ||
      360;
    const nextWidth = Math.max(1, measured);
    viewerViewportWidthRef.current = nextWidth;
    setViewerViewportWidth(nextWidth);
  }, []);

  useEffect(() => {
    if (!isImageViewerOpen) return;
    syncViewerViewportWidth();
    window.addEventListener("resize", syncViewerViewportWidth);
    return () => window.removeEventListener("resize", syncViewerViewportWidth);
  }, [isImageViewerOpen, syncViewerViewportWidth]);

  useEffect(() => {
    return () => {
      if (viewerSwipeTimerRef.current) {
        window.clearTimeout(viewerSwipeTimerRef.current);
      }
      if (viewerFadeTimerRef.current) {
        window.clearTimeout(viewerFadeTimerRef.current);
      }
    };
  }, []);

  const realPhotos = useMemo(() => {
    if (!item?.photos || item.photos.length === 0) return [];
    return item.photos;
  }, [item?.photos]);

  const hasRealPhotos = realPhotos.length > 0;

  const photos = useMemo(() => {
    if (realPhotos.length === 0) return [placeholder];
    return realPhotos;
  }, [realPhotos]);

  useEffect(() => {
    if (!isImageViewerOpen || !hasRealPhotos || photos.length <= 1) return;
    photos.forEach((src) => {
      if (!src || viewerPreloadedPhotosRef.current.has(src)) return;
      viewerPreloadedPhotosRef.current.add(src);
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });
  }, [hasRealPhotos, isImageViewerOpen, photos]);

  const currentImage = photos[Math.min(activePhoto, photos.length - 1)] || placeholder;
  const viewerImage = photos[Math.min(viewerPhotoIndex, photos.length - 1)] || placeholder;
  const viewerPrevImage = photos[(viewerPhotoIndex - 1 + photos.length) % photos.length] || placeholder;
  const viewerNextImage = photos[(viewerPhotoIndex + 1) % photos.length] || placeholder;
  const owner = (item as any)?.idOwner;
  const ownerId = typeof owner === "string" ? owner : owner?.id || owner?._id || null;
  const isCurrentUserOwner = Boolean(ownerId && currentUserId && ownerId === currentUserId);
  const normalizedUserRole = String(user?.role || "user").toLowerCase();
  const canModerateAd = normalizedUserRole === "admin" || normalizedUserRole === "moderator";
  useEffect(() => {
    if (!user || isCurrentUserOwner || commentsDisabled) return;
    void refreshCommentCaptcha();
  }, [commentsDisabled, isCurrentUserOwner, refreshCommentCaptcha, user]);
  const reviewCountByAuthor = useMemo(() => {
    const map = new Map<string, number>();
    [...comments, ...stayReviews].forEach((item) => {
      const author = typeof item?.authorName === "string" && item.authorName.trim()
        ? item.authorName.trim()
        : t("adDetails.owner");
      map.set(author, (map.get(author) || 0) + 1);
    });
    return map;
  }, [comments, stayReviews, t]);
  const displayedCommentRating = commentHoverRating ?? commentRating;
  const ownerName = owner
    ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() || t("adDetails.owner")
    : t("adDetails.owner");
  const rawOwnerIndicatif = String(owner?.indicatif || (item as any)?.indicatif || "").trim();
  const ownerIndicatifDigits = rawOwnerIndicatif.replace(/[^\d]/g, "");
  const ownerIndicatif = ownerIndicatifDigits ? `+${ownerIndicatifDigits}` : "";
  const ownerPhoneOnly = String(owner?.phone || "").trim();
  const ownerPhone = `${ownerIndicatif} ${ownerPhoneOnly}`.trim() || t("adDetails.ownerFallbackPhone");
  const ownerPhoneCompact = `${ownerIndicatif}${ownerPhoneOnly}`.replace(/\s+/g, "");
  const ownerPhonePretty = formatPhoneForDisplay(ownerPhoneCompact || ownerPhone);
  const ownerPhoneDial = `${ownerIndicatif}${ownerPhoneOnly}`.replace(/[^\d+]/g, "");
  const currencyCode = String(item?.currency || "EUR").toUpperCase();
  const calendarCurrencyUnit = currencyCode;
  const formattedPrice = `${new Intl.NumberFormat(locale).format(Number(item?.price || 0))} ${currencyCode}`;
  const localizedTitle = getLocalizedText((item as any)?.title_i18n, locale);
  const localizedContent = getLocalizedText((item as any)?.content_i18n, locale);
  const displayContent = capitalizeFirstLetter(localizedContent);
  const shouldUseIconListDescription = Boolean((item as any)?.descriptionIconListEnabled);
  const smartDescription = useMemo(
    () => shouldUseIconListDescription ? splitSmartDescription(displayContent) : { intro: displayContent, items: [] },
    [displayContent, shouldUseIconListDescription]
  );
  const localizedAddress = capitalizeFirstLetter(cleanRepeatedAddressSegments(
    getLocalizedText((item as any)?.adress_i18n, locale, item?.adress || "")
  ));
  const locationCoordinates = useMemo(() => {
    const isUsableCoordinatePair = (latitude: number, longitude: number) =>
      Number.isFinite(latitude) && Number.isFinite(longitude) && (latitude !== 0 || longitude !== 0);

    const apiLatitude = Number((item as any)?.coordinates?.latitude);
    const apiLongitude = Number((item as any)?.coordinates?.longitude);
    if (isUsableCoordinatePair(apiLatitude, apiLongitude)) {
      return { latitude: apiLatitude, longitude: apiLongitude };
    }

    const responseLatitude = Number((item as any)?.latitude);
    const responseLongitude = Number((item as any)?.longitude);
    if (isUsableCoordinatePair(responseLatitude, responseLongitude)) {
      return { latitude: responseLatitude, longitude: responseLongitude };
    }

    const quartierLatitude = Number((item as any)?.idQuartier?.coordinates?.latitude);
    const quartierLongitude = Number((item as any)?.idQuartier?.coordinates?.longitude);
    if (isUsableCoordinatePair(quartierLatitude, quartierLongitude)) {
      return { latitude: quartierLatitude, longitude: quartierLongitude };
    }

    const geometryCoordinates = Array.isArray((item as any)?.idQuartier?.geometry?.coordinates)
      ? (item as any).idQuartier.geometry.coordinates
      : Array.isArray((item as any)?.geometry?.coordinates)
        ? (item as any).geometry.coordinates
        : null;

    if (Array.isArray(geometryCoordinates) && geometryCoordinates.length >= 2) {
      const [longitudeRaw, latitudeRaw] = geometryCoordinates;
      const latitude = Number(latitudeRaw);
      const longitude = Number(longitudeRaw);
      if (isUsableCoordinatePair(latitude, longitude)) {
        return { latitude, longitude };
      }
    }

    return null;
  }, [item]);
  const locationMapContainerRef = useRef<HTMLDivElement | null>(null);
  const locationMapRef = useRef<L.Map | null>(null);
  const locationMarkerRef = useRef<L.Marker | null>(null);
  const particularities = useMemo<ParticularityIconKey[]>(
    () =>
      Array.from(
        new Set(
          (Array.isArray((item as any)?.particularites) ? (item as any).particularites : [])
            .map((value: unknown) => (typeof value === "string" ? normalizeParticularityKey(value) : null))
            .filter((value: ParticularityIconKey | null): value is ParticularityIconKey => Boolean(value))
        )
      ),
      [item]
    );
  const adIsBlocked = (item as any)?.status === false;
  const similarAds = useMemo(
    () => relatedAds.slice(0, 3).filter((relatedAd) => Boolean(relatedAd.id || (relatedAd as any)?._id)),
    [relatedAds]
  );
  const newsAds = useMemo(
    () => relatedAds.slice(3, 7).filter((relatedAd) => Boolean(relatedAd.id || (relatedAd as any)?._id)),
    [relatedAds]
  );
  const newsFeatureAd = useMemo(() => {
    const candidate = relatedAds[7] || relatedAds[0] || null;
    return candidate && (candidate.id || (candidate as any)?._id) ? candidate : null;
  }, [relatedAds]);
  const formatAdPrice = (adItem: Ad) => {
    const currentCurrency = adItem.currency || "EUR";
    return `${new Intl.NumberFormat(locale).format(Number(adItem.price || 0))} ${t(`currencies.${currentCurrency}`, { defaultValue: currentCurrency })}`;
  };

  useEffect(() => {
    if (!locationCoordinates || !locationMapContainerRef.current) {
      if (locationMapRef.current) {
        locationMapRef.current.remove();
        locationMapRef.current = null;
      }
      locationMarkerRef.current = null;
      return;
    }

    const mapTarget = locationMapContainerRef.current;
    if (!locationMapRef.current) {
      locationMapRef.current = L.map(mapTarget, {
        zoomControl: true,
        attributionControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(locationMapRef.current);
    }

    const map = locationMapRef.current;
    const latLng: L.LatLngTuple = [locationCoordinates.latitude, locationCoordinates.longitude];

    if (locationMarkerRef.current) {
      locationMarkerRef.current.setLatLng(latLng);
    } else {
      locationMarkerRef.current = L.marker(latLng, { icon: DEFAULT_LEAFLET_ICON }).addTo(map);
    }

    locationMarkerRef.current.bindPopup(localizedAddress || t("adDetails.addressUnknown"));
    map.setView(latLng, 15);
    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      if (locationMapRef.current) {
        locationMapRef.current.remove();
        locationMapRef.current = null;
      }
      locationMarkerRef.current = null;
    };
  }, [localizedAddress, locationCoordinates, t]);

  const handleSendMessageToOwner = () => {
    if (!ownerId || isCurrentUserOwner) return;
    window.dispatchEvent(
      new CustomEvent("open-header-chat", {
        detail: {
          interlocutorId: String(ownerId),
          interlocutorName: ownerName,
        },
      })
    );
  };

  const handleOpenOwnerReportModal = () => {
    if (!user) {
      toast.error(t("adDetails.commentLoginRequired"));
      return;
    }
    setOwnerReportModalOpen(true);
  };

  const handleSubmitOwnerReport = async () => {
    const adId = String(id || (item as any)?._id || item?.id || "").trim();
    const message = ownerReportMessage.trim();
    if (!message) {
      toast.error(t("adDetails.reportOwnerMessageRequired"));
      return;
    }
    if (!adId) {
      toast.error(t("adDetails.reportOwnerError"));
      return;
    }
    setOwnerReportSubmitting(true);
    try {
      await reportOwnerByAdApi(adId, message);
      setOwnerReportModalOpen(false);
      setOwnerReportMessage("");
      toast.success(t("adDetails.reportOwnerSuccess"));
    } catch (error: any) {
      const serverMsg = typeof error?.response?.data?.msg === "string" ? error.response.data.msg : "";
      toast.error(serverMsg || t("adDetails.reportOwnerError"));
    } finally {
      setOwnerReportSubmitting(false);
    }
  };

  const handleConfirmBlockAd = async () => {
    if (!id) return;
    setIsBlockingAd(true);
    try {
      await updateAdStatusApi(id, false);
      toast.success(t("adDetails.blockAdSuccess"));
      setIsBlockAdConfirmOpen(false);
      await dispatch(fetchAdDetailsById(id));
    } catch (_err) {
      toast.error(t("adDetails.blockAdError"));
    } finally {
      setIsBlockingAd(false);
    }
  };

  const handleConfirmValidateAd = async () => {
    if (!id) return;
    setIsValidatingAd(true);
    try {
      await updateAdStatusApi(id, true);
      toast.success(t("adDetails.validateAdSuccess"));
      setIsValidateAdConfirmOpen(false);
      await dispatch(fetchAdDetailsById(id));
    } catch (_err) {
      toast.error(t("adDetails.validateAdError"));
    } finally {
      setIsValidatingAd(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!id) return;
    if (commentsDisabled) return;
    if (!user) {
      toast.info(t("adDetails.commentLoginRequired"));
      return;
    }
    if (isCurrentUserOwner) return;
    if (commentRating < 1 || commentRating > 5) {
      toast.error(t("adDetails.commentRatingRequired"));
      return;
    }
    await refreshCommentCaptcha();
    setCommentCaptchaPopupOpen(true);
  };

  const closeCommentCaptchaPopup = () => {
    if (commentSubmitting) return;
    setCommentCaptchaPopupOpen(false);
    setCommentCaptchaError("");
  };

  const handleValidateCommentCaptcha = async () => {
    if (!id) return;
    if (commentsDisabled || !user || isCurrentUserOwner) return;
    const content = capitalizeCommentStart(commentContent);
    if (commentRating < 1 || commentRating > 5) {
      toast.error(t("adDetails.commentRatingRequired"));
      return;
    }
    const trimmedCaptcha = commentCaptchaInput.trim();
    if (!trimmedCaptcha) {
      setCommentCaptchaError(t("captcha.errorRequired"));
      return;
    }
    if (!commentCaptchaChallenge.proof) {
      setCommentCaptchaError(t("system.serverError"));
      return;
    }
    if (trimmedCaptcha.toUpperCase() !== commentCaptchaChallenge.code.toUpperCase()) {
      void refreshCommentCaptcha(t("captcha.errorInvalid"));
      return;
    }

    setCommentSubmitting(true);
    try {
      const response = await saveAdCommentApi(id, {
        rating: commentRating,
        content,
        captcha: {
          input: trimmedCaptcha,
          code: commentCaptchaChallenge.code,
          proof: String(commentCaptchaChallenge.proof || ""),
        },
      });
      const saved = response?.data?.data && typeof response.data.data === "object" ? response.data.data : null;
      if (saved && saved.valide === true) {
        setComments((prev) => {
          const withoutMine = prev.filter((item) => String(item?.userId || "") !== String(saved.userId || ""));
          return [saved, ...withoutMine];
        });
      }
      setCommentContent("");
      setCommentRating(1);
      setCommentCaptchaPopupOpen(false);
      await refreshCommentCaptcha();
      setCommentModerationPopupOpen(true);
    } catch (error: any) {
      if (String(error?.response?.data?.code || "").trim() === "CAPTCHA_INVALID") {
        void refreshCommentCaptcha(t("captcha.errorInvalid"));
      }
      const message =
        (typeof error?.response?.data?.msg === "string" && error.response.data.msg) ||
        (typeof error?.message === "string" && error.message) ||
        t("adDetails.commentSaveError");
      toast.error(message);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleConfirmDeleteAd = async () => {
    if (!id) return;
    setIsDeletingAd(true);
    try {
      await deleteAdCascadeApi(id);
      toast.success(t("adDetails.deleteAdSuccess"));
      setIsDeleteAdConfirmOpen(false);
      navigate("/", { replace: true });
    } catch (_err) {
      toast.error(t("adDetails.deleteAdError"));
    } finally {
      setIsDeletingAd(false);
    }
  };

  const handleCallOwner = () => {
    if (!ownerPhoneDial) return;
    window.location.href = `tel:${ownerPhoneDial}`;
  };

  const handleShowOwnerPhonePopup = () => {
    setIsOwnerPhonePopupOpen(true);
  };
  const secondVisibleMonth = useMemo(() => addMonths(visibleMonth, 1), [visibleMonth]);
  const firstMonthCells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const secondMonthCells = useMemo(() => buildMonthCells(secondVisibleMonth), [secondVisibleMonth]);
  const bookingDateEntries = useMemo(() => {
    return Array.isArray((item as any)?.bookingDates) ? (item as any).bookingDates : [];
  }, [item]);
  const currentUserReservationStatusByDate = useMemo(() => {
    const statusByDate = new Map<string, boolean>();
    if (!currentUserId) return statusByDate;

    bookingDateEntries.forEach((dateEntry: any) => {
      const dateKey = toDateKey(dateEntry?.date);
      if (!dateKey) return;

      const reservations = Array.isArray(dateEntry?.reservations) ? dateEntry.reservations : [];
      reservations.forEach((reservation: any) => {
        const reservationUserId = reservation?.user?.id || reservation?.user?._id;
        if (!reservationUserId || String(reservationUserId) !== String(currentUserId)) return;
        const booked = toBookedFlag(reservation);
        if (booked === null) return;
        statusByDate.set(dateKey, Boolean(booked));
      });
    });

    localPendingBookingDates.forEach((dateKey) => {
      if (!statusByDate.has(dateKey)) {
        statusByDate.set(dateKey, false);
      }
    });

    return statusByDate;
  }, [bookingDateEntries, currentUserId, localPendingBookingDates]);
  const serverCurrentUserPendingDates = useMemo(() => {
    const pendingDates = new Set<string>();
    if (!currentUserId) return pendingDates;

    bookingDateEntries.forEach((dateEntry: any) => {
      const dateKey = toDateKey(dateEntry?.date);
      if (!dateKey) return;
      const reservations = Array.isArray(dateEntry?.reservations) ? dateEntry.reservations : [];

      reservations.forEach((reservation: any) => {
        const reservationUserId = reservation?.user?.id || reservation?.user?._id;
        if (!reservationUserId || String(reservationUserId) !== String(currentUserId)) return;
        const booked = toBookedFlag(reservation);
        if (booked === false) pendingDates.add(dateKey);
      });
    });

    return pendingDates;
  }, [bookingDateEntries, currentUserId]);
  const userBookedDates = useMemo(() => {
    const bookedDates = new Set<string>();
    currentUserReservationStatusByDate.forEach((_booked, dateKey) => bookedDates.add(dateKey));
    return bookedDates;
  }, [currentUserReservationStatusByDate]);
  const userConfirmedBookedDates = useMemo(() => {
    const confirmedDates = new Set<string>();
    currentUserReservationStatusByDate.forEach((booked, dateKey) => {
      if (booked === true) confirmedDates.add(dateKey);
    });
    return confirmedDates;
  }, [currentUserReservationStatusByDate]);
  const userPendingBookedDates = useMemo(() => {
    const pendingDates = new Set<string>();
    currentUserReservationStatusByDate.forEach((booked, dateKey) => {
      if (booked === false) pendingDates.add(dateKey);
    });
    return pendingDates;
  }, [currentUserReservationStatusByDate]);
  const hasPendingBookedDates = userPendingBookedDates.size > 0;
  const ownerReservationStatusByDate = useMemo(() => {
    const statusMap = new Map<string, { hasReservation: boolean; hasConfirmed: boolean }>();
    bookingDateEntries.forEach((dateEntry: any) => {
      const dateKey = toDateKey(dateEntry?.date);
      if (!dateKey) return;

      const reservations = Array.isArray(dateEntry?.reservations) ? dateEntry.reservations : [];
      const current = statusMap.get(dateKey) ?? { hasReservation: false, hasConfirmed: false };
      let hasReservation = current.hasReservation;
      let hasConfirmed = current.hasConfirmed;

      reservations.forEach((reservation: any) => {
        const reservationBooked = toBookedFlag(reservation);
        if (reservationBooked === null) return;
        hasReservation = true;
        hasConfirmed = hasConfirmed || Boolean(reservationBooked);
      });

      statusMap.set(dateKey, { hasReservation, hasConfirmed });
    });

    return statusMap;
  }, [bookingDateEntries]);
  const adBookedDates = useMemo(() => {
    const bookedDates = new Set<string>();
    ownerReservationStatusByDate.forEach((status, dateKey) => {
      if (status.hasReservation) bookedDates.add(dateKey);
    });
    return bookedDates;
  }, [ownerReservationStatusByDate]);
  const adConfirmedBookedDates = useMemo(() => {
    const confirmedDates = new Set<string>();
    ownerReservationStatusByDate.forEach((status, dateKey) => {
      if (status.hasConfirmed) confirmedDates.add(dateKey);
    });
    return confirmedDates;
  }, [ownerReservationStatusByDate]);
  const ownerReservationOutlineByDate = useMemo(() => {
    const outlineByDate = new Map<string, { color: string; reservationKey: string }>();
    bookingDateEntries.forEach((dateEntry: any) => {
      const dateKey = toDateKey(dateEntry?.date);
      if (!dateKey) return;
      const reservations = Array.isArray(dateEntry?.reservations) ? dateEntry.reservations : [];
      const coloredReservation = reservations.find((reservation: any) => {
        const reservationBooked = toBookedFlag(reservation);
        return (
          reservationBooked !== null &&
          reservation?.blocked !== true &&
          typeof reservation?.reservationColor === "string" &&
          /^#[0-9A-Fa-f]{6}$/.test(reservation.reservationColor)
        );
      });
      if (coloredReservation?.reservationColor) {
        const bookingId = String(coloredReservation?.bookingId || "");
        const reservationGroup = String(coloredReservation?.reservationGroup || "");
        outlineByDate.set(dateKey, {
          color: coloredReservation.reservationColor,
          reservationKey: `${bookingId}-${reservationGroup || dateKey}`,
        });
      }
    });
    return outlineByDate;
  }, [bookingDateEntries]);
  const adBlockedDates = useMemo(() => {
    const blockedDates = new Set<string>();
    bookingDateEntries.forEach((entry: any) => {
      const dateKey = toDateKey(entry?.date);
      if (!dateKey) return;
      if (entry?.blocked === true) blockedDates.add(dateKey);
    });
    return blockedDates;
  }, [bookingDateEntries]);
  const activeBookedDates = useMemo(() => {
    return new Set<string>(userBookedDates);
  }, [userBookedDates]);
  const selectedOwnerActionDateKeys = useMemo(
    () => Array.from(selectedOwnerActionDates).sort((a, b) => a.localeCompare(b)),
    [selectedOwnerActionDates]
  );
  const selectedOwnerActionDateLabels = useMemo(
    () => selectedOwnerActionDateKeys.map((dateKey) => formatFullDateFromKey(dateKey, locale)),
    [locale, selectedOwnerActionDateKeys]
  );

  useEffect(() => {
    if (!isCalendarOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (isPricePopupOpen) {
        closePricePopup();
        return;
      }
      setIsCalendarOpen(false);
      setArrivalDate(null);
      setDepartureDate(null);
      setCalendarDateError("");
      setPricePopupView("menu");
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCalendarOpen, isPricePopupOpen]);

  useEffect(() => {
    if (!isCalendarOpen) {
      document.body.style.overflow = "";
      return;
    }

    setSelectedPendingCancelDates(new Set());
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isCalendarOpen]);

  useEffect(() => {
    setLocalPendingBookingDates(new Set());
  }, [currentUserId, id]);

  useEffect(() => {
    setLocalPendingBookingDates((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((dateKey) => {
        if (serverCurrentUserPendingDates.has(dateKey)) {
          next.add(dateKey);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [serverCurrentUserPendingDates]);

  useEffect(() => {
    if (isCurrentUserOwner) return;
    setSelectedPendingCancelDates((prev) => {
      const next = new Set<string>();
      Array.from(prev).forEach((dateKey) => {
        if (userPendingBookedDates.has(dateKey)) next.add(dateKey);
      });
      return next;
    });
    if (!hasPendingBookedDates) {
      setSelectedPendingCancelDates(new Set());
    }
  }, [isCurrentUserOwner, hasPendingBookedDates, userPendingBookedDates]);

  useEffect(() => {
    if (!isCurrentUserOwner && isPricePopupOpen) {
      closePricePopup();
    }
  }, [isCurrentUserOwner, isPricePopupOpen]);

  const handleDatePick = (pickedDate: Date, inCurrentMonth: boolean) => {
    if (!inCurrentMonth) return;
    const normalizedDate = normalizeDate(pickedDate);
    const dateKey = formatDateForApi(normalizedDate);
    if (normalizedDate.getTime() <= today.getTime()) return;
    const isConfirmedBookedByOthers =
      !isCurrentUserOwner &&
      adConfirmedBookedDates.has(dateKey) &&
      !userConfirmedBookedDates.has(dateKey);
    if (adBlockedDates.has(dateKey)) {
      setCalendarDateError(t("adDetails.dateBlockedByOwner"));
      return;
    }
    if (isConfirmedBookedByOthers) {
      setCalendarDateError(t("adDetails.dateAlreadyBooked"));
      return;
    }
    if (!isCurrentUserOwner && userPendingBookedDates.has(dateKey)) {
      setCalendarDateError("");
      setSelectedPendingCancelDates((prev) => {
        const next = new Set(prev);
        if (next.has(dateKey)) next.delete(dateKey);
        else next.add(dateKey);
        return next;
      });
      setArrivalDate(null);
      setDepartureDate(null);
      return;
    }
    if (activeBookedDates.has(dateKey)) {
      return;
    }
    if (selectedPendingCancelDates.size > 0) {
      setSelectedPendingCancelDates(new Set());
    }
    setCalendarDateError("");

    if (!arrivalDate || departureDate) {
      setArrivalDate(normalizedDate);
      setDepartureDate(null);
      return;
    }

    if (normalizedDate.getTime() <= arrivalDate.getTime()) {
      setArrivalDate(normalizedDate);
      setDepartureDate(null);
      return;
    }

    setDepartureDate(normalizedDate);
  };

  const clearDates = (force = false) => {
    if (!force) {
      setSelectedPendingCancelDates(new Set());
    }
    setArrivalDate(null);
    setDepartureDate(null);
    setCalendarDateError("");
  };

  const closeCalendarModal = () => {
    setIsCalendarOpen(false);
    setIsColorLegendPopupOpen(false);
    closePricePopup();
    setSelectedOwnerActionDates(new Set());
    setSelectedOwnerActionCategory(null);
    setPricePopupView("menu");
    clearDates(true);
  };

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setIsMobileCalendar(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    setHasViewedSecondCalendarMonth(false);
  }, [isCalendarOpen]);

  const openImageViewer = (index: number) => {
    if (viewerSwipeTimerRef.current) {
      window.clearTimeout(viewerSwipeTimerRef.current);
      viewerSwipeTimerRef.current = null;
    }
    if (viewerFadeTimerRef.current) {
      window.clearTimeout(viewerFadeTimerRef.current);
      viewerFadeTimerRef.current = null;
    }
    setViewerDragTransitionMs(0);
    setViewerDragTransitionEasing("ease-out");
    setViewerDragOffsetX(0);
    setViewerTrackOpacity(1);
    setViewerTrackOpacityTransitionMs(0);
    viewerViewportWidthRef.current =
      (typeof window !== "undefined" ? Math.max(1, window.innerWidth || 360) : 360);
    setViewerViewportWidth(viewerViewportWidthRef.current);
    if (!hasRealPhotos) return;
    setViewerPhotoIndex(index);
    setIsImageViewerOpen(true);
  };

  const closeImageViewer = () => {
    if (viewerSwipeTimerRef.current) {
      window.clearTimeout(viewerSwipeTimerRef.current);
      viewerSwipeTimerRef.current = null;
    }
    if (viewerFadeTimerRef.current) {
      window.clearTimeout(viewerFadeTimerRef.current);
      viewerFadeTimerRef.current = null;
    }
    setViewerDragTransitionMs(0);
    setViewerDragTransitionEasing("ease-out");
    setViewerDragOffsetX(0);
    setViewerTrackOpacity(1);
    setViewerTrackOpacityTransitionMs(0);
    viewerIsAnimatingRef.current = false;
    setIsImageViewerOpen(false);
  };

  const animateViewerWithFade = (direction: 1 | -1) => {
    if (viewerIsAnimatingRef.current || photos.length <= 1) return;
    viewerIsAnimatingRef.current = true;

    const pauseBeforeSwitchMs = 110;
    const settleAfterSwitchMs = 120;

    setViewerDragTransitionMs(0);
    setViewerDragOffsetX(0);

    viewerFadeTimerRef.current = window.setTimeout(() => {
      setViewerPhotoIndex((prev) =>
        direction === 1 ? (prev + 1) % photos.length : (prev - 1 + photos.length) % photos.length
      );

      viewerFadeTimerRef.current = window.setTimeout(() => {
        viewerIsAnimatingRef.current = false;
        viewerFadeTimerRef.current = null;
      }, settleAfterSwitchMs + 20);
    }, pauseBeforeSwitchMs + 10);
  };

  const animateViewerWithSlide = (direction: 1 | -1, velocityBoost = false) => {
    if (viewerIsAnimatingRef.current || photos.length <= 1) return;
    syncViewerViewportWidth();
    const width = Math.max(1, viewerViewportWidthRef.current || window.innerWidth || 360);
    const duration = velocityBoost ? 160 : 210;
    const easing = "cubic-bezier(0.22, 0.61, 0.36, 1)";
    const targetOffset = direction === 1 ? -width : width;

    viewerIsAnimatingRef.current = true;
    setViewerTrackOpacity(1);
    setViewerTrackOpacityTransitionMs(0);
    setViewerDragTransitionEasing(easing);
    setViewerDragTransitionMs(duration);
    setViewerDragOffsetX(targetOffset);

    if (viewerSwipeTimerRef.current) {
      window.clearTimeout(viewerSwipeTimerRef.current);
    }
    viewerSwipeTimerRef.current = window.setTimeout(() => {
      setViewerPhotoIndex((prev) =>
        direction === 1 ? (prev + 1) % photos.length : (prev - 1 + photos.length) % photos.length
      );
      setViewerDragTransitionMs(0);
      setViewerDragOffsetX(0);
      viewerIsAnimatingRef.current = false;
      viewerSwipeTimerRef.current = null;
    }, duration + 16);
  };

  const showPrevViewerImage = () => animateViewerWithFade(-1);
  const showNextViewerImage = () => animateViewerWithFade(1);

  const handleViewerTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) return;
    if (viewerIsAnimatingRef.current) return;
    if (viewerSwipeTimerRef.current) {
      window.clearTimeout(viewerSwipeTimerRef.current);
      viewerSwipeTimerRef.current = null;
    }
    if (viewerFadeTimerRef.current) {
      window.clearTimeout(viewerFadeTimerRef.current);
      viewerFadeTimerRef.current = null;
    }
    const measuredWidth =
      viewerViewportRef.current?.clientWidth ||
      event.currentTarget.clientWidth ||
      window.innerWidth ||
      360;
    viewerViewportWidthRef.current = Math.max(1, measuredWidth);
    setViewerViewportWidth(viewerViewportWidthRef.current);
    const startX = event.touches[0].clientX;
    const startY = event.touches[0].clientY;
    viewerTouchStartXRef.current = startX;
    viewerTouchCurrentXRef.current = startX;
    viewerTouchStartYRef.current = startY;
    viewerTouchStartTimeRef.current = performance.now();
    viewerTouchLockAxisRef.current = null;
    setViewerDragTransitionMs(0);
    setViewerDragTransitionEasing("linear");
    setViewerTrackOpacity(1);
    setViewerTrackOpacityTransitionMs(0);
  };

  const handleViewerTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (viewerTouchStartXRef.current === null || event.touches.length !== 1) return;
    if (viewerIsAnimatingRef.current) return;
    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = currentX - viewerTouchStartXRef.current;
    const deltaY = currentY - (viewerTouchStartYRef.current ?? currentY);

    if (viewerTouchLockAxisRef.current === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
      viewerTouchLockAxisRef.current = Math.abs(deltaX) >= Math.abs(deltaY) ? "x" : "y";
    }
    if (viewerTouchLockAxisRef.current !== "x") return;

    event.preventDefault();
    viewerTouchCurrentXRef.current = currentX;
    const maxDrag = Math.max(120, viewerViewportWidthRef.current * 0.38);
    const clampedDeltaX = Math.max(-maxDrag, Math.min(maxDrag, deltaX * 0.94));
    setViewerDragOffsetX(clampedDeltaX);
  };

  const handleViewerTouchEnd = () => {
    const startX = viewerTouchStartXRef.current;
    const endX = viewerTouchCurrentXRef.current;
    const startTime = viewerTouchStartTimeRef.current;
    viewerTouchStartXRef.current = null;
    viewerTouchCurrentXRef.current = null;
    viewerTouchStartYRef.current = null;
    viewerTouchStartTimeRef.current = null;
    const lockedAxis = viewerTouchLockAxisRef.current;
    viewerTouchLockAxisRef.current = null;
    if (lockedAxis && lockedAxis !== "x") return;
    if (startX === null || endX === null) return;

    const deltaX = endX - startX;
    const elapsedMs = startTime ? Math.max(1, performance.now() - startTime) : 240;
    const velocity = Math.abs(deltaX) / elapsedMs;
    const viewportWidth = Math.max(320, viewerViewportWidthRef.current || window.innerWidth || 360);
    const swipeThreshold = Math.max(44, viewportWidth * 0.14);
    const fastSwipeVelocityThreshold = 0.62;

    if (Math.abs(deltaX) < swipeThreshold && velocity < fastSwipeVelocityThreshold) {
      setViewerDragTransitionEasing("cubic-bezier(0.22, 0.61, 0.36, 1)");
      setViewerDragTransitionMs(180);
      setViewerDragOffsetX(0);
      return;
    }

    const direction: 1 | -1 = deltaX < 0 ? 1 : -1;
    animateViewerWithSlide(direction, velocity >= fastSwipeVelocityThreshold);
  };

  const handleViewerTouchCancel = () => {
    viewerTouchStartXRef.current = null;
    viewerTouchCurrentXRef.current = null;
    viewerTouchStartYRef.current = null;
    viewerTouchStartTimeRef.current = null;
    viewerTouchLockAxisRef.current = null;
    setViewerDragTransitionEasing("cubic-bezier(0.22, 0.61, 0.36, 1)");
    setViewerDragTransitionMs(180);
    setViewerDragOffsetX(0);
  };

  function closePricePopup() {
    setPricePopupView("menu");
    setIsPricePopupOpen(false);
    setSelectedPriceDate(null);
    setSelectedOwnerActionDates(new Set());
    setSelectedOwnerActionCategory(null);
  }

  const resetSignupForm = () => {
    setSignupData({
      email: "",
      password: "",
      confirme_password: "",
      firstName: "",
      lastName: "",
      phone: "",
      codeCountry: "",
      indicatif: "",
      status: "",
      nbr_child: "",
      country: "",
    });
    setSignupErrors({});
    setSignupCaptchaInput("");
    setSignupPhoneMaskDigits(null);
  };

  const handleOwnerActionDateToggle = (dateKey: string, category: string) => {
    setSelectedOwnerActionDates((prev) => {
      const isAlreadySelected = prev.has(dateKey);
      if (isAlreadySelected) {
        const next = new Set(prev);
        next.delete(dateKey);
        if (next.size === 0) setSelectedOwnerActionCategory(null);
        return next;
      }

      if (selectedOwnerActionCategory && selectedOwnerActionCategory !== category) {
        setSelectedOwnerActionCategory(category);
        return new Set([dateKey]);
      }

      const next = new Set(prev);
      next.add(dateKey);
      setSelectedOwnerActionCategory(category);
      return next;
    });
  };

  const handleOwnerNext = () => {
    if (!selectedOwnerActionDateKeys.length) return;
    const firstDate = new Date(`${selectedOwnerActionDateKeys[0]}T00:00:00`);
    if (Number.isNaN(firstDate.getTime())) return;
    setSelectedPriceDate(normalizeDate(firstDate));
    const shouldOpenReservationsDirectly =
      selectedOwnerActionCategory === "owner_pending" ||
      selectedOwnerActionCategory === "owner_confirmed";
    setPricePopupView(shouldOpenReservationsDirectly ? "reservationsList" : "menu");
    setIsPricePopupOpen(true);
  };

  const isSelectedDateBookedForOwner = useMemo(() => {
    if (!selectedPriceDate) return false;
    return adBookedDates.has(formatDateForApi(normalizeDate(selectedPriceDate)));
  }, [selectedPriceDate, adBookedDates]);
  const selectedDateReservationUsers = useMemo(() => {
    const targetDateKeys = isCurrentUserOwner && selectedOwnerActionDateKeys.length > 0
      ? selectedOwnerActionDateKeys
      : (selectedPriceDate ? [formatDateForApi(normalizeDate(selectedPriceDate))] : []);

    if (!targetDateKeys.length) return [];

    const usersById = new Map<string, ReservationUserName>();
    targetDateKeys.forEach((targetDateKey) => {
      const targetDateEntry = bookingDateEntries.find((entry: any) => toDateKey(entry?.date) === targetDateKey);
      const reservations = Array.isArray(targetDateEntry?.reservations) ? targetDateEntry.reservations : [];

      reservations.forEach((reservation: any) => {
        const bookedForSelectedDate = toBookedFlag(reservation);
        if (bookedForSelectedDate === null) return;
        const reservationUser = reservation?.user;
        const reservationUserId = reservationUser?.id || reservationUser?._id;
        if (!reservationUserId) return;

        const existingEntry = usersById.get(reservationUserId);
        if (!existingEntry) {
          usersById.set(reservationUserId, {
            id: reservationUserId,
            firstName: reservationUser?.firstName || reservationUser?.firstname || "",
            lastName: reservationUser?.lastName || reservationUser?.lastname || "",
            email: reservationUser?.email || "",
            phone: reservationUser?.phone || "",
            status: reservationUser?.status || "",
            nbr_child:
              Number.isInteger(Number(reservationUser?.nbr_child)) ? Number(reservationUser?.nbr_child) : null,
            country: reservationUser?.country || "",
            indicatif: reservationUser?.indicatif || "",
            codeCountry: reservationUser?.codeCountry || "",
            reservedDates: [{ dateKey: targetDateKey, booked: bookedForSelectedDate }],
            hasPendingDates: !bookedForSelectedDate,
            hasConfirmedDates: Boolean(bookedForSelectedDate),
          });
          return;
        }

        const hasDateAlready = existingEntry.reservedDates.some((entry) => entry.dateKey === targetDateKey);
        const nextReservedDates = hasDateAlready
          ? existingEntry.reservedDates.map((entry) =>
              entry.dateKey === targetDateKey ? { ...entry, booked: bookedForSelectedDate } : entry
            )
          : [...existingEntry.reservedDates, { dateKey: targetDateKey, booked: bookedForSelectedDate }];
        usersById.set(reservationUserId, {
          ...existingEntry,
          firstName: existingEntry.firstName || reservationUser?.firstName || reservationUser?.firstname || "",
          lastName: existingEntry.lastName || reservationUser?.lastName || reservationUser?.lastname || "",
          email: existingEntry.email || reservationUser?.email || "",
          phone: existingEntry.phone || reservationUser?.phone || "",
          status: existingEntry.status || reservationUser?.status || "",
          nbr_child:
            existingEntry.nbr_child ??
            (Number.isInteger(Number(reservationUser?.nbr_child)) ? Number(reservationUser?.nbr_child) : null),
          country: existingEntry.country || reservationUser?.country || "",
          indicatif: existingEntry.indicatif || reservationUser?.indicatif || "",
          codeCountry: existingEntry.codeCountry || reservationUser?.codeCountry || "",
          reservedDates: nextReservedDates,
          hasPendingDates: nextReservedDates.some((entry) => !entry.booked),
          hasConfirmedDates: nextReservedDates.some((entry) => entry.booked),
        });
      });
    });

    return Array.from(usersById.values())
      .map((entry) => ({
        ...entry,
        reservedDates: [...entry.reservedDates].sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
      }))
      .filter((reservationUser) => reservationUser.firstName || reservationUser.lastName);
  }, [
    bookingDateEntries,
    isCurrentUserOwner,
    selectedOwnerActionDateKeys,
    selectedPriceDate,
  ]);
  const usersReservedTitleDate = useMemo(() => {
    const targetDateKey = isCurrentUserOwner && selectedOwnerActionDateKeys.length > 0
      ? selectedOwnerActionDateKeys[0]
      : (selectedPriceDate ? formatDateForApi(normalizeDate(selectedPriceDate)) : "");
    if (!targetDateKey) return "";
    const parsed = new Date(`${targetDateKey}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return targetDateKey;
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(parsed);
  }, [isCurrentUserOwner, locale, selectedOwnerActionDateKeys, selectedPriceDate]);

  useEffect(() => {
    setExpandedReservationUserId(null);
  }, [selectedPriceDate]);

  const ownerDatePriceMap = useMemo(() => {
    const map = new Map<string, number>();
    bookingDateEntries.forEach((entry: any) => {
      const dateKey = toDateKey(entry?.date);
      if (!dateKey) return;

      const entryLevelPrice = Number(entry?.priceByDay);
      if (Number.isFinite(entryLevelPrice) && entryLevelPrice > 0) {
        map.set(dateKey, entryLevelPrice);
        return;
      }

      const reservations = Array.isArray(entry?.reservations) ? entry.reservations : [];
      const ownerReservation = reservations.find((reservation: any) => {
        const reservationUserId = reservation?.user?.id || reservation?.user?._id;
        return ownerId && reservationUserId && String(reservationUserId) === String(ownerId);
      });

      const fallbackReservation = ownerReservation || reservations[0];
      const entryPrice = Number(fallbackReservation?.priceByDay);
      if (Number.isFinite(entryPrice) && entryPrice > 0) map.set(dateKey, entryPrice);
    });

    return map;
  }, [bookingDateEntries, ownerId]);

  const getDatePrice = (dateKey: string) => {
    if (datePriceOverrides[dateKey] !== undefined) return datePriceOverrides[dateKey];
    const fromOwnerReservation = ownerDatePriceMap.get(dateKey);
    if (typeof fromOwnerReservation === "number") return fromOwnerReservation;
    return Number(item?.price || 0);
  };

  const handlePriceValidation = async () => {
    if (!selectedPriceDate || !item?.id) {
      toast.error(t("adDetails.dateNotFound"));
      return;
    }
    const adId = item.id;
    const connectedUserId = user?.id || user?._id;
    if (!connectedUserId) {
      toast.error(t("adDetails.cannotGetUserId"));
      return;
    }

    const normalized = Number(String(pricePopupValue).replace(",", "."));
    if (Number.isNaN(normalized) || normalized <= 0) {
      toast.error(t("adDetails.invalidPrice"));
      return;
    }

    const targetDateKey = formatDateForApi(normalizeDate(selectedPriceDate));
    const ownerTargetDateKeys = isCurrentUserOwner && selectedOwnerActionDateKeys.length > 0
      ? selectedOwnerActionDateKeys
      : [targetDateKey];

    try {
      await runWithApiLoader(async () => {
        await dispatch(
          updateBookingPriceByDate({
            id_user: connectedUserId,
            id_ads: adId,
            ...(ownerTargetDateKeys.length > 1
              ? { bookingDates: ownerTargetDateKeys }
              : { date: ownerTargetDateKeys[0] }),
            priceByDay: normalized,
          })
        ).unwrap();
      });

      setDatePriceOverrides((prev) => {
        const next = { ...prev };
        ownerTargetDateKeys.forEach((dateKey) => {
          next[dateKey] = normalized;
        });
        return next;
      });
      closePricePopup();

      if (ownerTargetDateKeys.length > 1) {
        toast.success(t("adDetails.dailyPriceSavedForSelectedDates", { count: ownerTargetDateKeys.length }), { autoClose: 6000 });
      } else {
        toast.success(
          t("adDetails.dailyPriceSavedForDate", {
            date: formatLongDate(selectedPriceDate, locale, t("adDetails.selectDate")),
          }),
          { autoClose: 6000 }
        );
      }
    } catch (error: any) {
      const message =
        (typeof error === "string" && error) ||
        error?.message ||
        t("adDetails.updatePriceError");
      toast.error(message, { autoClose: 6000 });
    }
  };

  const handleOwnerSetBlockedStateForSelectedDates = async (blocked: boolean) => {
    const adId = item?.id || (item as any)?._id;
    if (!adId) {
      toast.error(t("adDetails.adNotFound"));
      return;
    }
    const connectedUserId = user?.id || user?._id;
    if (!connectedUserId) {
      toast.error(t("adDetails.cannotGetUserId"));
      return;
    }
    if (!selectedOwnerActionDateKeys.length) {
      toast.error(t("adDetails.selectAtLeastOneDate"));
      return;
    }

    try {
      await runWithApiLoader(async () => {
        await dispatch(
          setBookingDatesBlockedByOwner({
            id_user: connectedUserId,
            id_ads: adId,
            bookingDates: selectedOwnerActionDateKeys,
            blocked,
          })
        ).unwrap();
      });
      await runWithApiLoader(async () => {
        if (id) {
          await dispatch(fetchAdDetailsById(id));
        }
      });
      closePricePopup();
      toast.success(blocked ? t("adDetails.datesBlocked") : t("adDetails.datesUnblocked"));
    } catch (error: any) {
      const message =
        (typeof error === "string" && error) ||
        error?.message ||
        t("adDetails.blockDatesError");
      toast.error(message);
    }
  };

  const handleSignupInputChange = (event: any) => {
    const target = event.target;
    const name = target.name;
    const value = target.value;
    const mask = target.mask || "";
    const indicatif = target.indicatif || "";

    setSignupData((prev) => {
      if (name === "status") {
        const normalizedStatus = normalizeUserStatus(value);
        return {
          ...prev,
          status: normalizedStatus,
          nbr_child: normalizedStatus === "married_with_children" ? prev.nbr_child : "",
          indicatif,
          country: target.country || prev.country,
          codeCountry: target.code || prev.codeCountry,
        };
      }

      if (name === "nbr_child") {
        return {
          ...prev,
          nbr_child: value === "" ? "" : Number(value),
          indicatif,
          country: target.country || prev.country,
          codeCountry: target.code || prev.codeCountry,
        };
      }

      return {
        ...prev,
        indicatif,
        country: target.country || prev.country,
        codeCountry: target.code || prev.codeCountry,
        [name]: value,
        ...(name === "phone" ? { indicatif } : {}),
      };
    });

    setSignupErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "phone" && mask) {
      const expectedDigits = (mask.match(/\d/g) || []).length;
      if (expectedDigits !== signupPhoneMaskDigits) {
        setSignupPhoneMaskDigits(expectedDigits);
      }
    }
  };

  const resolveSignupErrorMessage = (error: any): string => {
    if (typeof error === "string" && error.trim()) return error.trim();
    const apiMessage = error?.response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage.trim();
    if (apiMessage && typeof apiMessage === "object") {
      const currentLang = (i18n.resolvedLanguage || "fr").split("-")[0];
      const byLang = (apiMessage as Record<string, unknown>)[currentLang];
      if (typeof byLang === "string" && byLang.trim()) return byLang.trim();
      const fallback =
        (apiMessage as Record<string, unknown>).fr ||
        (apiMessage as Record<string, unknown>).en ||
        (apiMessage as Record<string, unknown>).ar;
      if (typeof fallback === "string" && fallback.trim()) return fallback.trim();
    }
    if (typeof error?.response?.data?.msg === "string" && error.response.data.msg.trim()) {
      return error.response.data.msg.trim();
    }
    if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();
    return t("system.registerError");
  };

  const buildSignupPayload = ({ includeCredentials = true }: { includeCredentials?: boolean } = {}) => ({
    email: signupData.email.trim(),
    firstName: signupData.firstName,
    lastName: signupData.lastName,
    phone: signupData.phone.trim(),
    codeCountry: signupData.codeCountry,
    indicatif: signupData.indicatif,
    country: signupData.country,
    status: signupData.status,
    nbr_child: signupData.status === "married_with_children" ? Number(signupData.nbr_child) : 0,
    ...(includeCredentials ? {
      password: signupData.password,
      captcha: {
        input: signupCaptchaInput.trim(),
        code: signupCaptchaChallenge.code,
        proof: String(signupCaptchaChallenge.proof || ""),
      },
    } : {}),
  });

  const validateSignupForm = ({ ownerClient = false }: { ownerClient?: boolean } = {}): CalendarSignupErrors => {
    const formErrors: CalendarSignupErrors = {};

    if (!signupData.lastName.trim()) formErrors.lastName = t("adDetails.errors.lastNameRequired");
    if (!signupData.firstName.trim()) formErrors.firstName = t("adDetails.errors.firstNameRequired");
    if (!ownerClient && !signupData.email.trim()) formErrors.email = t("adDetails.errors.emailRequired");
    else if (signupData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      formErrors.email = t("adDetails.errors.emailInvalid");
    }
    if (!signupData.status.trim()) formErrors.status = t("adDetails.errors.statusRequired");
    if (signupData.status === "married_with_children") {
      if (
        signupData.nbr_child === "" ||
        !Number.isInteger(Number(signupData.nbr_child)) ||
        Number(signupData.nbr_child) < 0 ||
        Number(signupData.nbr_child) > 7
      ) {
        formErrors.nbr_child = t("adDetails.errors.nbrChildRequired");
      }
    }
    if (!ownerClient && !signupData.password.trim()) formErrors.password = t("adDetails.errors.passwordRequired");
    else if (signupData.password.trim() && signupData.password.length < 6) {
      formErrors.password = t("adDetails.errors.passwordMin");
    }
    if (!ownerClient && signupData.confirme_password !== signupData.password) {
      formErrors.confirme_password = t("adDetails.errors.passwordMismatch");
    }
    if (!ownerClient && !signupData.phone.trim()) {
      formErrors.phone = t("adDetails.errors.phoneRequired");
    } else if (hasLeadingZeroPhoneDigit(signupData.phone)) {
      formErrors.phone = t("adDetails.errors.phoneLeadingZero");
    } else if (signupData.phone.trim() && signupPhoneMaskDigits && signupPhoneMaskDigits > 0) {
      const enteredDigits = signupData.phone.replace(/\D/g, "").length;
      if (enteredDigits !== signupPhoneMaskDigits) {
        formErrors.phone = t("adDetails.errors.phoneExact", { count: signupPhoneMaskDigits });
      }
    }
    if (!ownerClient && !signupCaptchaInput.trim()) {
      formErrors.captcha = t("captcha.errorRequired");
    } else if (!ownerClient && !signupCaptchaChallenge.proof) {
      formErrors.captcha = t("system.serverError");
    } else if (!ownerClient && signupCaptchaInput.trim().toUpperCase() !== signupCaptchaChallenge.code.toUpperCase()) {
      formErrors.captcha = t("captcha.errorInvalid");
    }

    return formErrors;
  };

  const handleSaveBooking = async (): Promise<boolean> => {
    if (!item?.id) {
      toast.error(t("adDetails.adNotFound"));
      if (!user) refreshSignupCaptcha();
      return false;
    }
    if (!arrivalDate) {
      setCalendarDateError(t("adDetails.selectArrivalDate"));
      toast.error(t("adDetails.selectAtLeastOneDate"));
      if (!user) refreshSignupCaptcha();
      return false;
    }

    const effectiveDepartureDate = departureDate || arrivalDate;
    const selectedBookingDates = getDateRange(arrivalDate, effectiveDepartureDate);
    if (!selectedBookingDates.length) {
      toast.error(t("adDetails.noDateSelected"));
      if (!user) refreshSignupCaptcha();
      return false;
    }

    if (selectedBookingDates.length < minimumNights) {
      const message = t("adDetails.minimumNightsMessage", { count: minimumNights });
      setCalendarDateError(message);
      setIsMinimumNightsPopupOpen(true);
      if (!user) refreshSignupCaptcha();
      return false;
    }

    let payload: any = {
      id_ads: item.id,
      bookingDates: selectedBookingDates,
      priceByDay: getDatePrice(selectedBookingDates[0]),
      booked: false,
    };

    if (user) {
      const connectedUserId = user.id || user._id;
      if (!connectedUserId) {
        toast.error(t("adDetails.cannotGetUserId"));
        return false;
      }
      payload = {
        ...payload,
        id_user: connectedUserId,
      };
    } else {
      const formErrors = validateSignupForm();
      if (Object.keys(formErrors).length > 0) {
        setSignupErrors(formErrors);
        toast.error(t("adDetails.invalidUserForm"));
        refreshSignupCaptcha();
        return false;
      }

      const signupPayload = buildSignupPayload();

      const ownerEmail = typeof owner?.email === "string" ? owner.email.trim().toLowerCase() : "";
      const requestedEmail = signupData.email.trim().toLowerCase();
      if (ownerEmail && requestedEmail && ownerEmail === requestedEmail) {
        toast.error(t("adDetails.ownerCannotBookOwnAd"));
        refreshSignupCaptcha();
        return false;
      }

      try {
        await runWithApiLoader(async () => {
          await registerApi(signupPayload);
        });
        await dispatch(fetchCurrentUser()).unwrap();
      } catch (signupError: any) {
        const signupErrorMessage = resolveSignupErrorMessage(signupError);
        const statusCode = Number(signupError?.response?.status || 0);
        if (statusCode === 409) {
          setSignupErrors((prev) => ({ ...prev, email: signupErrorMessage }));
        }
        toast.error(signupErrorMessage, { autoClose: 3000 });
        refreshSignupCaptcha();
        return false;
      }

      payload = {
        ...payload,
        user: signupPayload,
      };
    }

    setIsBookingSubmitting(true);

    try {
      await runWithApiLoader(async () => {
        await dispatch(saveBooking(payload)).unwrap();
      });
      setLocalPendingBookingDates((prev) => {
        const next = new Set(prev);
        selectedBookingDates.forEach((dateKey) => next.add(dateKey));
        return next;
      });
      clearDates(true);
      setIsCalendarOpen(false);
      if (!user) {
        await refreshSignupCaptcha();
      }
      toast.success(t("adDetails.bookingSuccess"), {
        autoClose: 3000,
        style: {
          background: "#1f9d55",
          color: "#ffffff",
        },
      });
      return true;
    } catch (bookingError: any) {
      const message =
        (typeof bookingError === "string" && bookingError) ||
        bookingError?.message ||
        t("adDetails.saveBookingError");
      const normalizedMessage = `${message.trim().charAt(0).toUpperCase()}${message.trim().slice(1)}`.replace(/\s*$/, "");
      const finalMessage = /[.!?]$/.test(normalizedMessage) ? normalizedMessage : `${normalizedMessage}.`;
      toast.error(finalMessage, {
        autoClose: 3000,
      });
      if (!user) refreshSignupCaptcha();
      return false;
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleOwnerReserveForPrivateUser = async (): Promise<boolean> => {
    if (!item?.id) {
      toast.error(t("adDetails.adNotFound"));
      refreshSignupCaptcha();
      return false;
    }
    const adId = item.id;
    if (!selectedOwnerActionDateKeys.length) {
      toast.error(t("adDetails.selectAtLeastOneDate"));
      refreshSignupCaptcha();
      return false;
    }

    const formErrors = validateSignupForm({ ownerClient: true });
    if (Object.keys(formErrors).length > 0) {
      setSignupErrors(formErrors);
      toast.error(t("adDetails.invalidUserForm"));
      refreshSignupCaptcha();
      return false;
    }

    const { email: _email, ...signupPayload } = buildSignupPayload({ includeCredentials: false });
    setIsBookingSubmitting(true);

    try {
      await runWithApiLoader(async () => {
        await dispatch(
          saveBooking({
            id_ads: adId,
            bookingDates: selectedOwnerActionDateKeys,
            priceByDay: getDatePrice(selectedOwnerActionDateKeys[0]),
            booked: true,
            user: signupPayload,
          })
        ).unwrap();
      });

      setPricePopupView("ownerSignupSuccess");
      setSelectedOwnerActionDates(new Set());
      setSelectedOwnerActionCategory(null);
      resetSignupForm();
      await refreshSignupCaptcha();
      if (id) {
        await runWithApiLoader(async () => {
          await dispatch(fetchAdDetailsById(id)).unwrap();
        });
      }
      return true;
    } catch (error: any) {
      const message = resolveSignupErrorMessage(error);
      const statusCode = Number(error?.response?.status || 0);
      if (statusCode === 409) {
        setSignupErrors((prev) => ({ ...prev, email: message }));
      }
      toast.error(message, { autoClose: 3000 });
      refreshSignupCaptcha();
      return false;
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleDeleteSelectedPendingDates = async (): Promise<boolean> => {
    if (!item?.id) {
      toast.error(t("adDetails.adNotFound"));
      return false;
    }
    const adId = item.id;
    if (selectedPendingCancelDates.size === 0) {
      toast.error(t("adDetails.selectLightRedDate"));
      return false;
    }

    const connectedUserId = user?.id || user?._id;
    if (!connectedUserId) {
      toast.error(t("adDetails.cannotGetUserIdNoAccent"));
      return false;
    }

    setIsBookingSubmitting(true);
    try {
      await runWithApiLoader(async () => {
        await dispatch(
          cancelBookingByDates({
            id_user: connectedUserId,
            id_ads: adId,
            bookingDates: Array.from(selectedPendingCancelDates),
          })
        ).unwrap();
      });

      setSelectedPendingCancelDates(new Set());
      setLocalPendingBookingDates((prev) => {
        const next = new Set(prev);
        selectedPendingCancelDates.forEach((dateKey) => next.delete(dateKey));
        return next;
      });
      setCalendarDateError("");
      await runWithApiLoader(async () => {
        await dispatch(fetchCurrentUser());
        if (id) {
          await dispatch(fetchAdDetailsById(id));
        }
      });
      toast.success(t("adDetails.datesDeleted"), {
        autoClose: 3000,
      });
      return true;
    } catch (deleteError: any) {
      const message =
        (typeof deleteError === "string" && deleteError) ||
        deleteError?.message ||
        t("adDetails.deleteDatesError");
      toast.error(message);
      return false;
    } finally {
      setIsBookingSubmitting(false);
    }
  };

  const handleConfirmCalendar = async () => {
    const hasDatesToDelete = selectedPendingCancelDates.size > 0;
    const hasDatesToReserve = Boolean(arrivalDate);

    if (hasDatesToDelete) {
      const deleteSuccess = await handleDeleteSelectedPendingDates();
      if (!deleteSuccess) return;
    }

    if (hasDatesToReserve) {
      await handleSaveBooking();
      return;
    }

    if (!hasDatesToDelete) {
      toast.error(t("adDetails.noActionToConfirm"));
    }
  };

  const hasPendingDatesToDelete = selectedPendingCancelDates.size > 0;
  const hasDatesToReserve = Boolean(arrivalDate);
  const hasCalendarActionForGuest = hasPendingDatesToDelete || hasDatesToReserve;
  const isInFirstCalendarMonth = (date: Date) =>
    date.getFullYear() === visibleMonth.getFullYear() && date.getMonth() === visibleMonth.getMonth();
  const selectedCalendarDateKeys = isCurrentUserOwner
    ? selectedOwnerActionDateKeys
    : Array.from(selectedPendingCancelDates);
  const selectionOnlyInFirstMonth = isCurrentUserOwner || hasPendingDatesToDelete
    ? selectedCalendarDateKeys.length > 0 && selectedCalendarDateKeys.every((key) => isInFirstCalendarMonth(new Date(`${key}T00:00:00`))) &&
      (!arrivalDate || isInFirstCalendarMonth(arrivalDate)) && (!departureDate || isInFirstCalendarMonth(departureDate))
    : Boolean(arrivalDate && isInFirstCalendarMonth(arrivalDate) && (!departureDate || isInFirstCalendarMonth(departureDate)));
  const showFirstMonthContinue = isMobileCalendar && selectionOnlyInFirstMonth && !hasViewedSecondCalendarMonth;
  const shouldFloatCalendarFooter = isMobileCalendar && !showFirstMonthContinue;

  const updateCalendarFooterFloating = useCallback(() => {
    if (!shouldFloatCalendarFooter || !calendarScrollRef.current || !calendarFooterRef.current) {
      setIsCalendarFooterFloating(false);
      return;
    }

    const containerRect = calendarScrollRef.current.getBoundingClientRect();
    const footerRect = calendarFooterRef.current.getBoundingClientRect();
    const footerVisible =
      footerRect.top < containerRect.bottom - 12 &&
      footerRect.bottom > containerRect.top + 12;

    setIsCalendarFooterFloating(!footerVisible);
  }, [shouldFloatCalendarFooter]);

  const handleCalendarScroll = (event: React.UIEvent<HTMLDivElement>) => {
    updateCalendarFooterFloating();
    if (!isMobileCalendar || hasViewedSecondCalendarMonth || !secondCalendarMonthRef.current) return;
    const container = event.currentTarget;
    if (container.scrollTop > 24 &&
        secondCalendarMonthRef.current.getBoundingClientRect().top < container.getBoundingClientRect().bottom - 120) {
      setHasViewedSecondCalendarMonth(true);
    }
  };

  useEffect(() => {
    if (!isCalendarOpen) {
      setIsCalendarFooterFloating(false);
      return;
    }

    const frameId = window.requestAnimationFrame(updateCalendarFooterFloating);
    window.addEventListener("resize", updateCalendarFooterFloating);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateCalendarFooterFloating);
    };
  }, [
    arrivalDate,
    departureDate,
    hasViewedSecondCalendarMonth,
    isCalendarOpen,
    selectedOwnerActionDateKeys.length,
    selectedPendingCancelDates.size,
    showFirstMonthContinue,
    updateCalendarFooterFloating,
  ]);

  const renderCalendarContinue = (afterFirstMonth = false) => (
    <button
      type="button"
      className={isCurrentUserOwner ? styles.ownerNextBtn : styles.confirmBtn}
      disabled={isCurrentUserOwner ? selectedOwnerActionDateKeys.length === 0 : isBookingSubmitting || !hasCalendarActionForGuest}
      onClick={() => {
        if (afterFirstMonth && !user) {
          setHasViewedSecondCalendarMonth(true);
          requestAnimationFrame(() => calendarSignupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
        } else if (isCurrentUserOwner) {
          handleOwnerNext();
        } else {
          void handleConfirmCalendar();
        }
      }}
    >
      {isCurrentUserOwner ? t("adDetails.nextStep") : isBookingSubmitting
        ? selectedPendingCancelDates.size > 0 ? t("adDetails.deleting") : t("adDetails.confirming")
        : t("adDetails.confirm")}
    </button>
  );
  const isDateInVisibleMonths = useCallback(
    (dateKey: string) => {
      const parsed = new Date(`${dateKey}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return false;
      if (normalizeDate(parsed).getTime() <= today.getTime()) return false;
      const month = parsed.getMonth();
      const year = parsed.getFullYear();
      const visibleMonthA = visibleMonth.getMonth();
      const visibleYearA = visibleMonth.getFullYear();
      const visibleMonthB = secondVisibleMonth.getMonth();
      const visibleYearB = secondVisibleMonth.getFullYear();
      return (month === visibleMonthA && year === visibleYearA) || (month === visibleMonthB && year === visibleYearB);
    },
    [secondVisibleMonth, today, visibleMonth],
  );
  const pendingBookedNightsCount = useMemo(() => {
    let count = 0;
    userPendingBookedDates.forEach((dateKey) => {
      if (isDateInVisibleMonths(dateKey)) count += 1;
    });
    return count;
  }, [isDateInVisibleMonths, userPendingBookedDates]);
  const confirmedBookedNightsCount = useMemo(() => {
    let count = 0;
    userConfirmedBookedDates.forEach((dateKey) => {
      if (isDateInVisibleMonths(dateKey)) count += 1;
    });
    return count;
  }, [isDateInVisibleMonths, userConfirmedBookedDates]);

  const renderMonth = (month: Date, cells: CalendarCell[], monthRef?: React.Ref<HTMLDivElement>) => (
    <div className={styles.calendarMonth} ref={monthRef}>
      <h4>{formatMonthTitle(month, locale)}</h4>
      <div className={`${styles.calendarWeekRow} ${isArabicLocale ? styles.calendarWeekRowArabic : ""}`}>
        {weekDays.map((day, index) => (
          <span key={`${month.getMonth()}-${index}`}>{day}</span>
        ))}
      </div>
      <div className={styles.calendarGrid}>
        {cells.map((cell, index) => {
          if (!cell.inCurrentMonth) {
            return (
              <div key={`${month.getMonth()}-${index}`} className={styles.calendarDayWrap}>
                <div className={`${styles.calendarDay} ${styles.dayPlaceholder}`} aria-hidden="true" />
              </div>
            );
          }

          const isSelectableDay = true;
          const isPastDay = normalizeDate(cell.date).getTime() <= today.getTime();
          const dateKey = formatDateForApi(normalizeDate(cell.date));
          const isBookedByCurrentUser = activeBookedDates.has(dateKey);
          const isBlockedDate = adBlockedDates.has(dateKey);
          const isConfirmedBookedByCurrentUser = userConfirmedBookedDates.has(dateKey);
          const isConfirmedBookedByOthers =
            !isConfirmedBookedByCurrentUser && !isCurrentUserOwner && adConfirmedBookedDates.has(dateKey);
          const isBookedForOwner = adBookedDates.has(dateKey);
          const isConfirmedBookedForOwner = adConfirmedBookedDates.has(dateKey);
          const ownerReservationOutline = isCurrentUserOwner && !isPastDay ? ownerReservationOutlineByDate.get(dateKey) : undefined;
          const ownerReservationColor = ownerReservationOutline?.color;
          const previousDateKey = addDaysToDateKey(dateKey, -1);
          const nextDateKey = addDaysToDateKey(dateKey, 1);
          const previousOutline = previousDateKey ? ownerReservationOutlineByDate.get(previousDateKey) : undefined;
          const nextOutline = nextDateKey ? ownerReservationOutlineByDate.get(nextDateKey) : undefined;
          const isCalendarRowStart = index % 7 === 0;
          const isCalendarRowEnd = index % 7 === 6;
          const hasPreviousOwnerReservationSegment =
            !!ownerReservationOutline &&
            !isCalendarRowStart &&
            previousOutline?.reservationKey === ownerReservationOutline.reservationKey;
          const hasNextOwnerReservationSegment =
            !!ownerReservationOutline &&
            !isCalendarRowEnd &&
            nextOutline?.reservationKey === ownerReservationOutline.reservationKey;
          const isDisabled = !cell.inCurrentMonth || isPastDay;
          const isOwnerSelected =
            isCurrentUserOwner &&
            selectedOwnerActionDates.has(dateKey);
          const isStart = isSelectableDay && !!arrivalDate && isSameDay(cell.date, arrivalDate);
          const isEnd = isSelectableDay && !!departureDate && isSameDay(cell.date, departureDate);
          const isInRange =
            isSelectableDay &&
            !!arrivalDate &&
            !!departureDate &&
            cell.date.getTime() > arrivalDate.getTime() &&
            cell.date.getTime() < departureDate.getTime();
          const isPendingBookedByCurrentUser =
            !isPastDay &&
            isSelectableDay &&
            !isCurrentUserOwner &&
            isBookedByCurrentUser &&
            !isConfirmedBookedByCurrentUser;
          const isConfirmedBookedByCurrentUserCell =
            !isPastDay && isSelectableDay && !isCurrentUserOwner && isConfirmedBookedByCurrentUser;
          const isConfirmedBookedByOthersCell =
            !isPastDay && isSelectableDay && isConfirmedBookedByOthers;
          const isPendingBookedForOwner =
            !isPastDay && isSelectableDay && isCurrentUserOwner && isBookedForOwner && !isConfirmedBookedForOwner;
          const isConfirmedBookedForOwnerCell =
            !isPastDay && isSelectableDay && isCurrentUserOwner && isConfirmedBookedForOwner;
          const isInSelectedRange = !isPastDay && isInRange;
          const isRangeStart = !isPastDay && isStart;
          const isRangeEnd = !isPastDay && isEnd;
          const isOwnerSelectedCell = !isPastDay && isOwnerSelected;
          const ownerDayCategory =
            isBlockedDate
              ? "blocked"
              : isPendingBookedForOwner
                ? "owner_pending"
                : isConfirmedBookedForOwnerCell
                  ? "owner_confirmed"
                  : "available";
          const hasFilledBackground =
            isPastDay ||
            isPendingBookedByCurrentUser ||
            isConfirmedBookedByCurrentUserCell ||
            isConfirmedBookedByOthersCell ||
            isPendingBookedForOwner ||
            isConfirmedBookedForOwnerCell ||
            isInSelectedRange ||
            isRangeStart ||
            isRangeEnd ||
            isOwnerSelectedCell;

          const dayClassName = [
            styles.calendarDay,
            !cell.inCurrentMonth ? styles.dayMuted : "",
            isPastDay ? styles.dayPast : "",
            isPendingBookedByCurrentUser ? styles.dayBookedByUser : "",
            isConfirmedBookedByCurrentUserCell ? styles.dayBookedByUserConfirmed : "",
            isConfirmedBookedByOthersCell ? styles.dayBookedByOtherConfirmed : "",
            isPendingBookedForOwner ? styles.dayBookedForOwner : "",
            isConfirmedBookedForOwnerCell ? styles.dayBookedForOwnerConfirmed : "",
            isBlockedDate ? styles.dayBlocked : "",
            isInSelectedRange ? styles.dayInRange : "",
            isRangeStart ? styles.dayStart : "",
            isRangeEnd ? styles.dayEnd : "",
            isOwnerSelectedCell ? styles.dayOwnerSelected : "",
            ownerReservationColor ? styles.dayOwnerReservationOutline : "",
            ownerReservationColor && !hasPreviousOwnerReservationSegment ? styles.dayOwnerReservationOutlineStart : "",
            ownerReservationColor && hasPreviousOwnerReservationSegment ? styles.dayOwnerReservationOutlineMiddle : "",
            ownerReservationColor && !hasNextOwnerReservationSegment ? styles.dayOwnerReservationOutlineEnd : "",
            !isCurrentUserOwner && selectedPendingCancelDates.has(dateKey) ? styles.dayPendingCancelSelected : "",
            !hasFilledBackground ? styles.dayTransparentFrame : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div key={`${month.getMonth()}-${index}`} className={styles.calendarDayWrap}>
              <button
                type="button"
                className={dayClassName}
                style={ownerReservationColor ? { "--reservation-color": ownerReservationColor } as React.CSSProperties : undefined}
                disabled={isDisabled}
                title={isBlockedDate ? t("adDetails.dateDisabledByOwnerTooltip") : undefined}
                onClick={() => {
                  if (isDisabled) return;
                  if (isCurrentUserOwner) {
                    handleOwnerActionDateToggle(dateKey, ownerDayCategory);
                    return;
                  }
                  handleDatePick(cell.date, cell.inCurrentMonth);
                }}
              >
                <span className={styles.calendarDayNumber}>{cell.date.getDate()}</span>
                {isSelectableDay && (
                  <span className={styles.calendarDayPrice}>
                    {new Intl.NumberFormat(locale).format(Number(getDatePrice(dateKey)))} {calendarCurrencyUnit}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
  

  return (
    <div className={styles.page}>
      <main className={styles.container}>
        {loading && <p className={styles.state}>{t("adDetails.loading")}</p>}
        {!loading && error && <p className={styles.state}>{t("adDetails.error", { error })}</p>}
        {!loading && !error && item && (
          <>
            <section className={styles.hero}>
              <ShimmerImage
                src={currentImage}
                alt={localizedTitle}
                className={styles.heroImage}
                onClick={hasRealPhotos ? () => openImageViewer(activePhoto) : undefined}
              />
              <div className={styles.overlay} />
              <div className={styles.heroContent}>
                <h1>{localizedTitle}</h1>
                <p>
                  {t("adDetails.heroStats", {
                    rooms: item.room_nbr || 0,
                    bathrooms: item.nbr_bathroom ?? 0,
                    area: item.area || 0,
                  })}
                </p>
              </div>
              <div className={styles.priceTag}>
                <strong>{formattedPrice}</strong>
                <span>{localizedAddress || t("adDetails.addressUnknown")}</span>
              </div>
            </section>

            {hasRealPhotos && (
              <section className={styles.gallery}>
                {photos.map((photo, index) => (
                  <button
                    key={`${photo}-${index}`}
                    type="button"
                    className={`${styles.thumb} ${index === activePhoto ? styles.thumbActive : ""}`}
                    onClick={() => {
                      setActivePhoto(index);
                      openImageViewer(index);
                    }}
                  >
                    <ShimmerImage src={photo} alt={t("adDetails.previewAlt", { index: index + 1 })} />
                  </button>
                ))}
              </section>
            )}

            <section className={styles.bookingBar}>
              <div>
                <h3>{t("adDetails.availabilityTitle")}</h3>
                <p>{t("adDetails.availabilityDescription")}</p>
              </div>
              <div className={styles.bookingBarActions}>
                <button type="button" onClick={() => setIsCalendarOpen(true)}>
                  {t("adDetails.showCalendar")}
                </button>
                {canModerateAd && adIsBlocked && (
                  <button type="button" className={styles.validateAdButton} onClick={() => setIsValidateAdConfirmOpen(true)}>
                    {t("adDetails.validateAd")}
                  </button>
                )}
                {canModerateAd && !adIsBlocked && (
                  <button type="button" className={styles.blockAdButton} onClick={() => setIsBlockAdConfirmOpen(true)}>
                    {t("adDetails.blockAd")}
                  </button>
                )}
                {canModerateAd && (
                  <button type="button" className={styles.deleteAdButton} onClick={() => setIsDeleteAdConfirmOpen(true)}>
                    {t("adDetails.deleteAd")}
                  </button>
                )}
                {(isCurrentUserOwner || canModerateAd) && (
                  <button type="button" onClick={() => navigate(`/add-ads/${id || ""}`)}>
                    {t("adDetails.editAd")}
                  </button>
                )}
              </div>
            </section>

            <section className={`${styles.contentGrid} ${isCurrentUserOwner ? styles.contentGridOwner : ""}`}>
              <article className={styles.left}>
                <h2>{t("adDetails.description")}</h2>
                {displayContent ? (
                  <div className={styles.descriptionText}>
                    {smartDescription.items.length > 0 ? (
                      <>
                        {smartDescription.intro && <p>{smartDescription.intro}</p>}
                        <ul className={styles.descriptionSmartList}>
                          {smartDescription.items.map((entry, index) => (
                            <li key={`${entry}-${index}`}>{entry}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p>{displayContent}</p>
                    )}
                  </div>
                ) : (
                  <p className={styles.descriptionText}>{t("adDetails.descriptionUnavailable")}</p>
                )}

                {particularities.length > 0 && (
                  <>
                      <div className={styles.particularitiesGrid}>
                        {particularities.map((key) => {
                          const Icon = PARTICULARITY_ICONS[key] || HomeIcon;
                          return (
                            <div key={key} className={styles.particularityCard}>
                              <span className={styles.particularityIcon} aria-hidden="true">
                              <Icon />
                            </span>
                            <span className={styles.particularityLabel}>
                              {t(`addAds.particularitiesOptions.${key}`, { defaultValue: key })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </article>

              <aside className={styles.middle}>
                <h3>{t("adDetails.features")}</h3>
                <ul>
                  <li>{item.area || 0} m2</li>
                  <li>{t("adDetails.rooms", { count: item.room_nbr || 0 })}</li>
                  <li>{t("adDetails.bathrooms", { count: item.nbr_bathroom ?? 0 })}</li>
                  <li>{t("adDetails.kitchens", { count: item.nbr_kitchen ?? 0 })}</li>
                  <li>{t("adDetails.parking")}</li>
                </ul>
              </aside>

                {!isCurrentUserOwner && (
                  <aside className={styles.agent}>
                    <h3>{t("adDetails.contactAgent")}</h3>
                    <h4>{ownerName}</h4>
                    <div className={styles.agentActions}>
                      {user && (
                        <button type="button" className={styles.messageOwnerButton} onClick={handleSendMessageToOwner}>
                          {t("adDetails.sendMessage")}
                        </button>
                      )}
                      {ownerId && (
                        <Link
                          to={`/owner/${ownerId}/ads`}
                          className={styles.ownerAdsLink}
                          state={{ ownerName }}
                        >
                          {t("adDetails.ownerAdsLink")}
                        </Link>
                      )}
                      {user && (
                        <button
                          type="button"
                          className={styles.reportOwnerButton}
                          onClick={handleOpenOwnerReportModal}
                        >
                          {t("adDetails.reportOwnerButton")}
                        </button>
                      )}
                      {!user && (
                        <button type="button" className={styles.showPhoneDesktopButton} onClick={handleShowOwnerPhonePopup}>
                          {t("adDetails.showOwnerPhone", { defaultValue: "Afficher le numéro de téléphone" })}
                        </button>
                      )}
                      <button type="button" className={styles.callOwnerActionButton} onClick={handleCallOwner}>
                        {t("adDetails.callOwner")}
                      </button>
                    </div>
                  </aside>
                )}
            </section>

            <section className={styles.addressSection}>
              <h2>{t("adDetails.addressLabel", { defaultValue: "Adresse" })}</h2>
              <p>{localizedAddress || t("adDetails.addressUnknown")}</p>
              {locationCoordinates && (
                <div className={styles.addressMapWrap}>
                  <div
                    ref={locationMapContainerRef}
                    className={styles.addressLeafletMap}
                    aria-label={t("adDetails.addressMapLabel", { defaultValue: "Carte de localisation" })}
                  />
                </div>
              )}
            </section>

            {(stayReviews.length > 0 || !commentsDisabled) && <section className={styles.commentsSection}>
              <div className={styles.commentsHeader}>
                <h3>{t("adDetails.commentsTitle")}</h3>
              </div>

              {user && !isCurrentUserOwner && (
                <div className={styles.commentComposer}>
                  <div className={styles.starRow}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`${styles.starButton} ${star <= displayedCommentRating ? styles.starButtonActive : ""}`}
                        onClick={() => setCommentRating(star)}
                        onMouseEnter={() => setCommentHoverRating(star)}
                        onMouseLeave={() => setCommentHoverRating(null)}
                        aria-label={t("adDetails.commentRate", { count: star })}
                      >
                        <span className={styles.starGlyph}>★</span>
                      </button>
                    ))}
                  </div>
                  <textarea
                    className={styles.commentTextarea}
                    rows={4}
                    value={commentContent}
                    onChange={(event) => setCommentContent(event.target.value)}
                    placeholder={t("adDetails.commentPlaceholder")}
                    disabled={commentSubmitting}
                  />
                  <button
                    type="button"
                    className={styles.commentSubmit}
                    onClick={handleSubmitComment}
                    disabled={commentSubmitting}
                  >
                    {commentSubmitting ? t("adDetails.commentSubmitting") : t("adDetails.commentSubmit")}
                  </button>
                </div>
              )}

              <div className={styles.commentList}>
                {commentsLoading ? (
                  <p className={styles.commentEmpty}>{t("adDetails.commentsLoading")}</p>
                ) : comments.length === 0 && stayReviews.length === 0 ? (
                  <p className={styles.commentEmpty}>{t("adDetails.commentsEmpty")}</p>
                ) : (
                  [...stayReviews, ...comments].map((comment) => {
                    const authorName = comment.authorName || t("adDetails.owner");
                    const authorReviewCount = reviewCountByAuthor.get(authorName) || 1;
                    const commentDateLabel = getCommentDateLabel(comment.createdAt, locale);
                    const { title, body } = splitCommentContent(comment.content);
                    const normalizedRating = Math.max(0, Math.min(5, Number(comment.rating || 0)));
                    const roundedRating = Math.round(normalizedRating);
                    return (
                      <article key={comment.id} className={styles.commentCard}>
                        <div className={styles.commentTop}>
                          <div className={styles.commentAuthorRow}>
                            <div className={styles.commentAvatar}>
                              <span className={styles.commentAvatarHead} />
                              <span className={styles.commentAvatarBody} />
                            </div>
                            <div className={styles.commentAuthorMeta}>
                              <strong className={styles.commentAuthorName}>{authorName}</strong>
                              <span className={styles.commentAuthorStats}>
                                {t("adDetails.commentReviews", { count: authorReviewCount })}
                              </span>
                            </div>
                          </div>
                          {commentDateLabel ? (
                            <span className={styles.commentTime}>{commentDateLabel}</span>
                          ) : null}
                        </div>
                        <div className={styles.commentRatingRow}>
                          <span className={styles.commentStars} aria-hidden="true">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span
                                key={star}
                                className={`${styles.commentStarGlyph} ${star <= roundedRating ? styles.commentStarActive : styles.commentStarInactive}`}
                              >
                                ★
                              </span>
                            ))}
                          </span>
                        </div>
                        <div className={styles.commentCardHeader}>
                          <strong>{title || comment.content}</strong>
                        </div>
                        {body ? <p>{body}</p> : null}
                      </article>
                    );
                  })
                )}
              </div>
            </section>}

            {similarAds.length > 0 && (
              <section className={styles.similarSection}>
                <h2>{t("adDetails.similar")}</h2>
                <div className={styles.similarGrid}>
                  {similarAds.map((relatedAd) => {
                    const relatedAdId = relatedAd.id || (relatedAd as any)?._id;
                    const relatedTitle = getLocalizedText(relatedAd.title_i18n, locale) || t("adDetails.addressUnknown");
                    const relatedImage = Array.isArray(relatedAd.photos) && relatedAd.photos[0] ? relatedAd.photos[0] : placeholder;
                    return (
                      <article
                        key={relatedAdId}
                        className={styles.similarCard}
                        onClick={() => navigate(`/ads/${relatedAdId}`)}
                      >
                        <ShimmerImage src={relatedImage} alt={relatedTitle} />
                        <div>
                          <h4>{relatedTitle}</h4>
                          <p>{formatAdPrice(relatedAd)}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {(newsAds.length > 0 || newsFeatureAd) && (
              <section className={styles.newsSection}>
                <h2>{t("adDetails.news")}</h2>
                <div className={styles.newsGrid}>
                  <div className={styles.newsCards}>
                    {newsAds.map((newsAd) => {
                      const newsAdId = newsAd.id || (newsAd as any)?._id;
                      const newsTitle = getLocalizedText(newsAd.title_i18n, locale) || t("adDetails.addressUnknown");
                      const newsImage = Array.isArray(newsAd.photos) && newsAd.photos[0] ? newsAd.photos[0] : placeholder;
                      return (
                        <article
                          key={newsAdId}
                          className={styles.newsCard}
                          onClick={() => navigate(`/ads/${newsAdId}`)}
                        >
                          <ShimmerImage src={newsImage} alt={newsTitle} />
                          <div>
                            <h4>{newsTitle}</h4>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/ads/${newsAdId}`);
                              }}
                            >
                              {t("adDetails.readArticle")}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  {newsFeatureAd ? (
                    <div
                      className={styles.newsFeatureWrap}
                      onClick={() => {
                        const newsFeatureId = newsFeatureAd.id || (newsFeatureAd as any)?._id;
                        if (!newsFeatureId) return;
                        navigate(`/ads/${newsFeatureId}`);
                      }}
                    >
                      <ShimmerImage
                        className={styles.newsFeature}
                        src={Array.isArray(newsFeatureAd.photos) && newsFeatureAd.photos[0] ? newsFeatureAd.photos[0] : placeholder}
                        alt={getLocalizedText(newsFeatureAd.title_i18n, locale) || t("adDetails.newsFeatureAlt")}
                      />
                      <div className={styles.newsFeatureOverlay}>
                        <h4>{getLocalizedText(newsFeatureAd.title_i18n, locale) || t("adDetails.newsFeatureAlt")}</h4>
                        <p>{formatAdPrice(newsFeatureAd)}</p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            )}

            {isCalendarOpen && (
              <div className={styles.calendarOverlay} onClick={closeCalendarModal}>
                <div className={styles.calendarModal} onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className={styles.calendarClose}
                    onClick={closeCalendarModal}
                    aria-label={t("adDetails.closeCalendar")}
                  >
                    ×
                  </button>
                  <div className={styles.calendarModalScroll} ref={calendarScrollRef} onScroll={handleCalendarScroll}>
                  <div className={styles.calendarMonths}>
                    {renderMonth(visibleMonth, firstMonthCells)}
                    {showFirstMonthContinue && (
                      <div className={styles.firstMonthContinue}>
                        {calendarDateError && <p className={styles.calendarDateError} role="alert">{calendarDateError}</p>}
                        {renderCalendarContinue(true)}
                      </div>
                    )}
                    {renderMonth(secondVisibleMonth, secondMonthCells, secondCalendarMonthRef)}
                  </div>
                  {calendarDateError && !showFirstMonthContinue && <p className={styles.calendarDateError}>{calendarDateError}</p>}
                  <div className={styles.calendarMonthNav}>
                    <button
                      type="button"
                      className={styles.monthMove}
                      onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
                    >
                      {t("adDetails.previousMonth")}
                    </button>
                    <button
                      type="button"
                      className={styles.monthMove}
                      onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
                    >
                      {t("adDetails.nextMonth")}
                    </button>
                  </div>
                  <button
                    type="button"
                    className={`${styles.colorLegendTrigger} ${isArabicLocale ? styles.rtlPhraseAlign : ""}`}
                    dir={isArabicLocale ? "rtl" : "ltr"}
                    onClick={() => setIsColorLegendPopupOpen(true)}
                  >
                    {t("adDetails.colorLegendTrigger")}
                  </button>
                  {isColorLegendPopupOpen && (
                    <div className={styles.colorLegendOverlay} onClick={() => setIsColorLegendPopupOpen(false)}>
                      <div className={styles.colorLegendModal} onClick={(event) => event.stopPropagation()}>
                        <h4>{t("adDetails.colorLegendTitle")}</h4>
                        <div className={styles.colorLegendList}>
                          <p>
                            <span className={`${styles.colorLegendDot} ${styles.colorLegendDotBrown}`} />
                            {t("adDetails.colorLegendBrown")}
                          </p>
                          <p>
                            <span className={`${styles.colorLegendDot} ${styles.colorLegendDotGreen}`} />
                            {t("adDetails.colorLegendGreen")}
                          </p>
                          <p>
                            <span className={`${styles.colorLegendDot} ${styles.colorLegendDotRed}`} />
                            {t("adDetails.colorLegendRed")}
                          </p>
                          <p>
                            <span className={`${styles.colorLegendDot} ${styles.colorLegendDotPurple}`} />
                            {t("adDetails.colorLegendPurple")}
                          </p>
                        </div>
                        <div className={styles.colorLegendActions}>
                          <button type="button" className={styles.clearBtn} onClick={() => setIsColorLegendPopupOpen(false)}>
                            {t("adDetails.close")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!user && (
                    <section className={styles.calendarSignup} ref={calendarSignupRef}>
                      <h4>{t("adDetails.userSignupTitle")}</h4>
                      <p>{t("adDetails.userSignupDescription")}</p>
                      <form className={styles.calendarSignupForm} onSubmit={(event) => event.preventDefault()}>
                        <div className={styles.calendarSignupGrid}>
                          <div className={styles.calendarFormRow}>
                            <div className={styles.formField}>
                              <label htmlFor="calendar-lastName">{t("adDetails.lastName")}</label>
                              <input
                                id="calendar-lastName"
                                type="text"
                                name="lastName"
                                value={signupData.lastName}
                                onChange={handleSignupInputChange}
                              />
                              {signupErrors.lastName && <small>{signupErrors.lastName}</small>}
                            </div>

                            <div className={styles.formField}>
                              <label htmlFor="calendar-firstName">{t("adDetails.firstName")}</label>
                              <input
                                id="calendar-firstName"
                                type="text"
                                name="firstName"
                                value={signupData.firstName}
                                onChange={handleSignupInputChange}
                              />
                              {signupErrors.firstName && <small>{signupErrors.firstName}</small>}
                            </div>
                          </div>

                          <div className={`${styles.calendarFormRow} ${signupData.status === "married_with_children" ? styles.calendarFormRowTriple : ""}`}>
                            <div className={styles.formField}>
                              <label htmlFor="calendar-email">{t("adDetails.email")}</label>
                              <input
                                id="calendar-email"
                                type="email"
                                name="email"
                                value={signupData.email}
                                onChange={handleSignupInputChange}
                              />
                              {signupErrors.email && <small>{signupErrors.email}</small>}
                            </div>

                            <div className={styles.formField}>
                              <label htmlFor="calendar-status">{t("adDetails.status")}</label>
                              <select
                                id="calendar-status"
                                name="status"
                                value={signupData.status}
                                onChange={handleSignupInputChange}
                              >
                                <option value="">{t("adDetails.statusPlaceholder")}</option>
                                <option value="single">{t("adDetails.statusSingle")}</option>
                                <option value="married">{t("adDetails.statusMarriedNoChildren")}</option>
                                <option value="married_with_children">{t("adDetails.statusMarriedWithChildren")}</option>
                              </select>
                              {signupErrors.status && <small>{signupErrors.status}</small>}
                            </div>

                            {signupData.status === "married_with_children" && (
                              <div className={styles.formField}>
                                <label htmlFor="calendar-nbr-child">{t("adDetails.nbrChild")}</label>
                                <select
                                  id="calendar-nbr-child"
                                  name="nbr_child"
                                  value={signupData.nbr_child}
                                  onChange={handleSignupInputChange}
                                >
                                  <option value="">{t("adDetails.nbrChildPlaceholder")}</option>
                                  {Array.from({ length: 8 }, (_, i) => i).map((value) => (
                                    <option key={value} value={value}>{value}</option>
                                  ))}
                                </select>
                                {signupErrors.nbr_child && <small>{signupErrors.nbr_child}</small>}
                              </div>
                            )}
                          </div>

                          <div className={styles.calendarFormRow}>
                            <div className={styles.formField}>
                              <label htmlFor="calendar-password">{t("adDetails.password")}</label>
                              <input
                                id="calendar-password"
                                type="password"
                                name="password"
                                value={signupData.password}
                                onChange={handleSignupInputChange}
                              />
                              {signupErrors.password && <small>{signupErrors.password}</small>}
                            </div>

                            <div className={styles.formField}>
                              <label htmlFor="calendar-confirme-password">{t("adDetails.confirmPassword")}</label>
                              <input
                                id="calendar-confirme-password"
                                type="password"
                                name="confirme_password"
                                value={signupData.confirme_password}
                                onChange={handleSignupInputChange}
                              />
                              {signupErrors.confirme_password && <small>{signupErrors.confirme_password}</small>}
                            </div>
                          </div>

                          <div className={styles.calendarFormRow}>
                            <div className={`${styles.formField} ${styles.formFieldPhone}`}>
                              <label htmlFor="calendar-phone">{t("adDetails.phone")}</label>
                              <PhoneInput onChange={handleSignupInputChange} value={signupData.phone} name="phone" />
                              {signupErrors.phone && <small>{signupErrors.phone}</small>}
                            </div>
                          </div>
                          <div className={styles.calendarFormRow}>
                            <div className={styles.formField}>
                              <SimpleCaptcha
                                idPrefix="calendar-signup"
                                challenge={signupCaptchaChallenge}
                                value={signupCaptchaInput}
                                onChange={(value) => {
                                  setSignupCaptchaInput(value);
                                  setSignupErrors((prev) => ({ ...prev, captcha: "" }));
                                }}
                                onRefresh={() => {
                                  setSignupErrors((prev) => ({ ...prev, captcha: "" }));
                                  void refreshSignupCaptcha();
                                }}
                                label={t("captcha.label")}
                                placeholder={t("captcha.placeholder")}
                                refreshLabel={t("captcha.refresh")}
                                error={signupErrors.captcha}
                              />
                            </div>
                          </div>
                        </div>
                      </form>
                    </section>
                  )}

                  {!isCurrentUserOwner && (
                    <div className={styles.calendarFooter} ref={calendarFooterRef}>
                      <div className={styles.footerCenter}>
                        {pendingBookedNightsCount > 0 && (
                          <div className={styles.footerPendingCount}>
                            {t("adDetails.pendingBookedNights", { count: pendingBookedNightsCount })}
                          </div>
                        )}
                        {confirmedBookedNightsCount > 0 && (
                          <div className={styles.footerConfirmedCount}>
                            {t("adDetails.confirmedBookedNights", { count: confirmedBookedNightsCount })}
                          </div>
                        )}
                      </div>
                      <div className={styles.footerActions}>
                        <button
                          type="button"
                          className={styles.clearBtn}
                          onClick={() => clearDates()}
                          disabled={isBookingSubmitting || !hasCalendarActionForGuest}
                        >
                          {t("adDetails.clear")}
                        </button>
                        {!showFirstMonthContinue && renderCalendarContinue()}
                      </div>
                    </div>
                  )}
                  {isCurrentUserOwner && (
                    <div className={`${styles.calendarFooter} ${styles.calendarFooterNoCenter}`} ref={calendarFooterRef}>
                      <div className={styles.footerActions}>
                        <button
                          type="button"
                          className={styles.clearBtn}
                          onClick={() => {
                            setSelectedOwnerActionDates(new Set());
                            setSelectedOwnerActionCategory(null);
                          }}
                          disabled={selectedOwnerActionDateKeys.length === 0}
                        >
                          {t("adDetails.clear")}
                        </button>
                        {!showFirstMonthContinue && renderCalendarContinue()}
                      </div>
                    </div>
                  )}
                  {isCalendarFooterFloating && (
                    <div className={`${styles.calendarFooter} ${styles.calendarFloatingFooter} ${isCurrentUserOwner ? styles.calendarFooterNoCenter : ""}`}>
                      {!isCurrentUserOwner && (
                        <div className={styles.footerCenter}>
                          {pendingBookedNightsCount > 0 && (
                            <div className={styles.footerPendingCount}>
                              {t("adDetails.pendingBookedNights", { count: pendingBookedNightsCount })}
                            </div>
                          )}
                          {confirmedBookedNightsCount > 0 && (
                            <div className={styles.footerConfirmedCount}>
                              {t("adDetails.confirmedBookedNights", { count: confirmedBookedNightsCount })}
                            </div>
                          )}
                        </div>
                      )}
                      <div className={styles.footerActions}>
                        <button
                          type="button"
                          className={styles.clearBtn}
                          onClick={() => {
                            if (isCurrentUserOwner) {
                              setSelectedOwnerActionDates(new Set());
                              setSelectedOwnerActionCategory(null);
                              return;
                            }
                            clearDates();
                          }}
                          disabled={isCurrentUserOwner ? selectedOwnerActionDateKeys.length === 0 : isBookingSubmitting || !hasCalendarActionForGuest}
                        >
                          {t("adDetails.clear")}
                        </button>
                        {renderCalendarContinue()}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
                {isPricePopupOpen && (
                  <div className={styles.dayPriceOverlay} onClick={(event) => event.stopPropagation()}>
                    <div
                      className={`${styles.dayPriceModal} ${
                        pricePopupView === "ownerSignup" || pricePopupView === "ownerSignupSuccess"
                          ? styles.ownerSignupModal
                          : ""
                      }`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={styles.dayPriceClose}
                        onClick={() => {
                          closePricePopup();
                        }}
                        aria-label={t("adDetails.closePriceWindow")}
                      >
                        ×
                      </button>
                      {pricePopupView === "menu" ? (
                        <div className={styles.dayPriceMenu}>
                          <p className={styles.dayPriceMenuTitle}>{t("adDetails.doYouWant")}</p>
                          {isCurrentUserOwner ? (
                            <>
                              {selectedOwnerActionCategory === "available" && (
                                <>
                                  <button
                                    type="button"
                                    className={styles.dayPriceMenuChoice}
                                    onClick={() => setPricePopupView("priceForm")}
                                  >
                                    1. {t("adDetails.modifyDatePrice")}
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.dayPriceMenuChoice}
                                    onClick={() => {
                                      void handleOwnerSetBlockedStateForSelectedDates(true);
                                    }}
                                  >
                                    2. {t("adDetails.blockSelectedDates")}
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.dayPriceMenuChoice}
                                    onClick={() => setPricePopupView("ownerSignup")}
                                  >
                                    3. {t("adDetails.ownerBookForPrivateUser")}
                                  </button>
                                </>
                              )}
                              {selectedOwnerActionCategory === "owner_pending" && (
                                <p className={styles.dayPriceReservationsEmpty}>
                                  {t("adDetails.noUserForDate")}
                                </p>
                              )}
                              {selectedOwnerActionCategory === "blocked" && (
                                <button
                                  type="button"
                                  className={styles.dayPriceMenuChoice}
                                  onClick={() => {
                                    void handleOwnerSetBlockedStateForSelectedDates(false);
                                  }}
                                >
                                  1. {t("adDetails.unblockSelectedDates")}
                                </button>
                              )}
                              {selectedOwnerActionCategory === "owner_confirmed" && (
                                <p className={styles.dayPriceReservationsEmpty}>
                                  {t("adDetails.noUserForDate")}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              {!isSelectedDateBookedForOwner && (
                                <button
                                  type="button"
                                  className={styles.dayPriceMenuChoice}
                                  onClick={() => setPricePopupView("priceForm")}
                                >
                                  1. {t("adDetails.modifyDatePrice")}
                                </button>
                              )}
                              {isSelectedDateBookedForOwner && (
                                <button
                                  type="button"
                                  className={styles.dayPriceMenuChoice}
                                  onClick={() => setPricePopupView("reservationsList")}
                                >
                                  1. {t("adDetails.validateReservation")}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      ) : pricePopupView === "priceForm" ? (
                        <>
                          <input
                            type="number"
                            min={1}
                            step="0.01"
                            value={pricePopupValue}
                            onChange={(event) => setPricePopupValue(event.target.value)}
                            placeholder={t("adDetails.priceInputPlaceholder")}
                          />
                          <button
                            type="button"
                            className={styles.dayPriceBackLink}
                            onClick={() => setPricePopupView("menu")}
                          >
                            {t("adDetails.backToMenu")}
                          </button>
                          <div className={styles.dayPriceActions}>
                            <button
                              type="button"
                              className={styles.dayPriceCancel}
                              onClick={() => {
                                closePricePopup();
                              }}
                            >
                              {t("adDetails.cancel")}
                            </button>
                            <button type="button" onClick={handlePriceValidation}>
                              {t("adDetails.confirm")}
                            </button>
                          </div>
                        </>
                      ) : pricePopupView === "ownerSignup" ? (
                        <section className={`${styles.calendarSignup} ${styles.ownerSignupPopup}`}>
                          <h4>{t("adDetails.ownerPrivateUserSignupTitle")}</h4>
                          <p style={{ marginBottom: "12px" }}>{t("adDetails.ownerPrivateUserSignupDescription", { count: selectedOwnerActionDateKeys.length })}</p>
                          {selectedOwnerActionDateLabels.length > 0 && (
                            <ul className={styles.ownerSignupSelectedDates}>
                              {selectedOwnerActionDateLabels.map((dateLabel) => (
                                <li key={dateLabel}>{dateLabel}</li>
                              ))}
                            </ul>
                          )}
                          <form
                            className={styles.calendarSignupForm}
                            onSubmit={(event) => {
                              event.preventDefault();
                              void handleOwnerReserveForPrivateUser();
                            }}
                          >
                            <div className={styles.calendarSignupGrid}>
                              <div className={`${styles.calendarFormRow} ${styles.ownerSignupFormRow}`}>
                                <div className={styles.formField}>
                                  <label htmlFor="owner-signup-lastName">{t("adDetails.lastName")}</label>
                                  <input
                                    id="owner-signup-lastName"
                                    type="text"
                                    name="lastName"
                                    value={signupData.lastName}
                                    onChange={handleSignupInputChange}
                                    disabled={isBookingSubmitting}
                                  />
                                  {signupErrors.lastName && <small>{signupErrors.lastName}</small>}
                                </div>

                                <div className={styles.formField}>
                                  <label htmlFor="owner-signup-firstName">{t("adDetails.firstName")}</label>
                                  <input
                                    id="owner-signup-firstName"
                                    type="text"
                                    name="firstName"
                                    value={signupData.firstName}
                                    onChange={handleSignupInputChange}
                                    disabled={isBookingSubmitting}
                                  />
                                  {signupErrors.firstName && <small>{signupErrors.firstName}</small>}
                                </div>
                              </div>

                              <div className={`${styles.calendarFormRow} ${styles.ownerSignupFormRow}`}>
                                <div className={styles.formField}>
                                  <label htmlFor="owner-signup-status">{t("adDetails.status")}</label>
                                  <select
                                    id="owner-signup-status"
                                    name="status"
                                    value={signupData.status}
                                    onChange={handleSignupInputChange}
                                    disabled={isBookingSubmitting}
                                  >
                                    <option value="">{t("adDetails.statusPlaceholder")}</option>
                                    <option value="single">{t("adDetails.statusSingle")}</option>
                                    <option value="married">{t("adDetails.statusMarriedNoChildren")}</option>
                                    <option value="married_with_children">{t("adDetails.statusMarriedWithChildren")}</option>
                                  </select>
                                  {signupErrors.status && <small>{signupErrors.status}</small>}
                                </div>

                                <div className={`${styles.formField} ${styles.formFieldPhone}`}>
                                  <label htmlFor="owner-signup-phone">{t("adDetails.phone")} ({t("adDetails.optional")})</label>
                                  <PhoneInput onChange={handleSignupInputChange} value={signupData.phone} name="phone" />
                                  {signupErrors.phone && <small>{signupErrors.phone}</small>}
                                </div>
                              </div>

                              {signupData.status === "married_with_children" && (
                                <div className={`${styles.calendarFormRow} ${styles.ownerSignupFormRow}`}>
                                  <div className={styles.formField}>
                                    <label htmlFor="owner-signup-nbr-child">{t("adDetails.nbrChild")}</label>
                                    <select
                                      id="owner-signup-nbr-child"
                                      name="nbr_child"
                                      value={signupData.nbr_child}
                                      onChange={handleSignupInputChange}
                                      disabled={isBookingSubmitting}
                                    >
                                      <option value="">{t("adDetails.nbrChildPlaceholder")}</option>
                                      {Array.from({ length: 8 }, (_, i) => i).map((value) => (
                                        <option key={value} value={value}>{value}</option>
                                      ))}
                                    </select>
                                    {signupErrors.nbr_child && <small>{signupErrors.nbr_child}</small>}
                                  </div>
                                </div>
                              )}

                            </div>
                            <button
                              type="submit"
                              className={styles.signupSubmit}
                              disabled={isBookingSubmitting}
                            >
                              {isBookingSubmitting ? t("adDetails.confirming") : t("adDetails.ownerBookForPrivateUser")}
                            </button>
                          </form>
                          <button
                            type="button"
                            className={styles.dayPriceBackLink}
                            onClick={() => setPricePopupView("menu")}
                            disabled={isBookingSubmitting}
                          >
                            {t("adDetails.backToMenu")}
                          </button>
                        </section>
                      ) : pricePopupView === "ownerSignupSuccess" ? (
                        <div className={styles.ownerSignupSuccess}>
                          <h4>{t("adDetails.ownerPrivateUserBookingSuccessTitle")}</h4>
                          <p>{t("adDetails.ownerPrivateUserBookingSuccessMessage")}</p>
                          <button type="button" onClick={closePricePopup}>
                            {t("adDetails.close")}
                          </button>
                        </div>
                      ) : (
                        <div className={styles.dayPriceReservations}>
                          <h5>{t("adDetails.usersReservedDate", { date: usersReservedTitleDate })}</h5>
                          {selectedDateReservationUsers.length ? (
                            <ul className={styles.dayPriceUserList}>
                              {selectedDateReservationUsers.map((reservationUser, index) => (
                                (() => {
                                  return (
                                    <li
                                      key={`${reservationUser.firstName}-${reservationUser.lastName}-${index}`}
                                      className={styles.dayPriceUserCard}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() =>
                                        setExpandedReservationUserId((prev) =>
                                          prev === reservationUser.id ? null : reservationUser.id
                                        )
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                          event.preventDefault();
                                          setExpandedReservationUserId((prev) =>
                                            prev === reservationUser.id ? null : reservationUser.id
                                          );
                                        }
                                      }}
                                    >
                                      <span className={styles.dayPriceUserAvatar}>
                                        {`${reservationUser.firstName?.[0] || ""}${reservationUser.lastName?.[0] || ""}`.toUpperCase()}
                                      </span>
                                      <span className={styles.dayPriceUserName}>
                                        {`${reservationUser.lastName} ${reservationUser.firstName}`.trim()}
                                      </span>
                                      <div
                                        className={`${styles.dayPriceUserDetails} ${
                                          expandedReservationUserId === reservationUser.id
                                            ? styles.dayPriceUserDetailsOpen
                                            : styles.dayPriceUserDetailsClosed
                                        }`}
                                        aria-hidden={expandedReservationUserId !== reservationUser.id}
                                      >
                                        <p>
                                          <strong>{t("adDetails.userEmailLabel")}</strong>{" "}
                                          {reservationUser.email || t("adDetails.userInfoUnknown")}
                                        </p>
                                        <p>
                                          <strong>{t("adDetails.userPhoneLabel")}</strong>{" "}
                                          {`${reservationUser.indicatif || ""} ${reservationUser.phone || ""}`.trim() ||
                                            t("adDetails.userInfoUnknown")}
                                        </p>
                                        <p>
                                          <strong>{t("adDetails.userStatusLabel")}</strong>{" "}
                                          {statusLabelByValue[normalizeUserStatus(reservationUser.status)] || t("adDetails.userInfoUnknown")}
                                        </p>
                                        <p>
                                          <strong>{t("adDetails.userChildrenLabel")}</strong>{" "}
                                          {reservationUser.nbr_child ?? t("adDetails.userInfoUnknown")}
                                        </p>
                                        <p>
                                          <strong>{t("adDetails.userCountryLabel")}</strong>{" "}
                                          {getLocalizedCountryLabel(reservationUser.codeCountry, reservationUser.country) || t("adDetails.userInfoUnknown")}
                                        </p>
                                        <p>
                                          <strong>{t("adDetails.userReservedDatesLabel")}</strong>
                                        </p>
                                        <ul className={styles.dayPriceUserDatesList}>
                                          {reservationUser.reservedDates.map((entry) => (
                                            <li key={`${reservationUser.id}-${entry.dateKey}`} className={styles.dayPriceUserDatesItem}>
                                              <span>{formatFullDateFromKey(entry.dateKey, locale)}</span>
                                              <span
                                                className={`${styles.dayPriceUserDateStatus} ${
                                                  entry.booked ? styles.dayPriceUserDateStatusConfirmed : styles.dayPriceUserDateStatusPending
                                                }`}
                                              >
                                                {entry.booked ? t("adDetails.statusConfirmed") : t("adDetails.statusPending")}
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </li>
                                  );
                                })()
                              ))}
                            </ul>
                          ) : (
                            <p className={styles.dayPriceReservationsEmpty}>{t("adDetails.noUserForDate")}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isOwnerPhonePopupOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setIsOwnerPhonePopupOpen(false)}>
                <div
                  className={`${styles.contactPromptModal} ${styles.ownerPhonePopupModal}`}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.ownerPhonePopupTitle}>{t("adDetails.ownerPhoneLabel")}</h4>
                  <p className={styles.ownerPhonePopupValue}>{ownerPhonePretty || ownerPhoneCompact || ownerPhone}</p>
                  <button
                    type="button"
                    className={styles.ownerPhonePopupClose}
                    onClick={() => setIsOwnerPhonePopupOpen(false)}
                  >
                    {t("adDetails.close")}
                  </button>
                </div>
              </div>
            )}
            {commentCaptchaPopupOpen && (
              <div className={styles.contactPromptOverlay} onClick={closeCommentCaptchaPopup}>
                <div
                  className={`${styles.contactPromptModal} ${styles.commentCaptchaModal}`}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.commentCaptchaTitle}>{t("adDetails.commentCaptchaTitle")}</h4>
                  <SimpleCaptcha
                    idPrefix="ad-comment-popup"
                    challenge={commentCaptchaChallenge}
                    value={commentCaptchaInput}
                    onChange={(value) => {
                      setCommentCaptchaInput(value);
                      if (commentCaptchaError) setCommentCaptchaError("");
                    }}
                    onRefresh={() => {
                      void refreshCommentCaptcha();
                    }}
                    label={t("captcha.label")}
                    placeholder={t("captcha.placeholder")}
                    refreshLabel={t("captcha.refresh")}
                    error={commentCaptchaError}
                    layout="stacked"
                  />
                  <div className={styles.commentCaptchaActions}>
                    <button
                      type="button"
                      className={styles.contactPromptClose}
                      onClick={closeCommentCaptchaPopup}
                      disabled={commentSubmitting}
                    >
                      {t("adDetails.close")}
                    </button>
                    <button
                      type="button"
                      className={styles.commentCaptchaValidate}
                      onClick={handleValidateCommentCaptcha}
                      disabled={commentSubmitting}
                    >
                      {commentSubmitting ? t("adDetails.commentCaptchaValidating") : t("adDetails.commentCaptchaValidate")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {commentModerationPopupOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setCommentModerationPopupOpen(false)}>
                <div
                  className={styles.contactPromptModal}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <p className={styles.contactPromptText}>{t("adDetails.commentModerationNotice")}</p>
                  <button
                    type="button"
                    className={styles.contactPromptClose}
                    onClick={() => setCommentModerationPopupOpen(false)}
                  >
                    {t("adDetails.close")}
                  </button>
                </div>
              </div>
            )}
            {isMinimumNightsPopupOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setIsMinimumNightsPopupOpen(false)}>
                <div
                  className={styles.contactPromptModal}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4>{t("adDetails.minimumNightsTitle")}</h4>
                  <p className={styles.contactPromptText}>{t("adDetails.minimumNightsMessage", { count: minimumNights })}</p>
                  <button
                    type="button"
                    className={styles.contactPromptClose}
                    onClick={() => setIsMinimumNightsPopupOpen(false)}
                  >
                    {t("adDetails.close")}
                  </button>
                </div>
              </div>
            )}
            {ownerReportModalOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => {
                if (ownerReportSubmitting) return;
                setOwnerReportModalOpen(false);
              }}>
                <div
                  className={`${styles.contactPromptModal} ${styles.ownerReportModal}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.ownerReportTitle}>{t("adDetails.reportOwnerTitle")}</h4>
                  <textarea
                    className={styles.ownerReportTextarea}
                    value={ownerReportMessage}
                    onChange={(event) => setOwnerReportMessage(event.target.value)}
                    rows={6}
                    maxLength={2000}
                    placeholder={t("adDetails.reportOwnerPlaceholder")}
                    disabled={ownerReportSubmitting}
                  />
                  <div className={styles.ownerReportActions}>
                    <button
                      type="button"
                      className={styles.contactPromptClose}
                      onClick={() => setOwnerReportModalOpen(false)}
                      disabled={ownerReportSubmitting}
                    >
                      {t("adDetails.reportOwnerCancel")}
                    </button>
                    <button
                      type="button"
                      className={styles.callOwnerButton}
                      onClick={handleSubmitOwnerReport}
                      disabled={ownerReportSubmitting}
                    >
                      {ownerReportSubmitting ? t("adDetails.reportOwnerSending") : t("adDetails.reportOwnerSubmit")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isBlockAdConfirmOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setIsBlockAdConfirmOpen(false)}>
                <div
                  className={`${styles.contactPromptModal} ${styles.blockAdConfirmModal}`}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.blockAdConfirmTitle}>{t("adDetails.blockAd")}</h4>
                  <p className={`${styles.contactPromptText} ${styles.blockAdConfirmText}`}>{t("adDetails.blockAdConfirmText")}</p>
                  <div className={styles.contactPromptActions}>
                    <button
                      type="button"
                      className={`${styles.contactPromptClose} ${styles.blockAdCancelBtn}`}
                      onClick={() => setIsBlockAdConfirmOpen(false)}
                      disabled={isBlockingAd}
                    >
                      {t("adDetails.no")}
                    </button>
                    <button
                      type="button"
                      className={styles.blockAdConfirmBtn}
                      onClick={handleConfirmBlockAd}
                      disabled={isBlockingAd}
                    >
                      {isBlockingAd ? t("adDetails.blockingAd") : t("adDetails.yes")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isValidateAdConfirmOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setIsValidateAdConfirmOpen(false)}>
                <div
                  className={`${styles.contactPromptModal} ${styles.validateAdConfirmModal}`}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.validateAdConfirmTitle}>{t("adDetails.validateAd")}</h4>
                  <p className={`${styles.contactPromptText} ${styles.validateAdConfirmText}`}>{t("adDetails.validateAdConfirmText")}</p>
                  <div className={styles.contactPromptActions}>
                    <button
                      type="button"
                      className={`${styles.contactPromptClose} ${styles.validateAdCancelBtn}`}
                      onClick={() => setIsValidateAdConfirmOpen(false)}
                      disabled={isValidatingAd}
                    >
                      {t("adDetails.no")}
                    </button>
                    <button
                      type="button"
                      className={styles.validateAdConfirmBtn}
                      onClick={handleConfirmValidateAd}
                      disabled={isValidatingAd}
                    >
                      {isValidatingAd ? t("adDetails.validatingAd") : t("adDetails.yes")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isDeleteAdConfirmOpen && (
              <div className={styles.contactPromptOverlay} onClick={() => setIsDeleteAdConfirmOpen(false)}>
                <div
                  className={`${styles.contactPromptModal} ${styles.deleteAdConfirmModal}`}
                  role="dialog"
                  aria-modal="true"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h4 className={styles.deleteAdConfirmTitle}>{t("adDetails.deleteAd")}</h4>
                  <p className={`${styles.contactPromptText} ${styles.deleteAdConfirmText}`}>{t("adDetails.deleteAdConfirmText")}</p>
                  <div className={styles.contactPromptActions}>
                    <button
                      type="button"
                      className={`${styles.contactPromptClose} ${styles.deleteAdCancelBtn}`}
                      onClick={() => setIsDeleteAdConfirmOpen(false)}
                      disabled={isDeletingAd}
                    >
                      {t("adDetails.no")}
                    </button>
                    <button
                      type="button"
                      className={styles.deleteAdConfirmBtn}
                      onClick={handleConfirmDeleteAd}
                      disabled={isDeletingAd}
                    >
                      {isDeletingAd ? t("adDetails.deletingAd") : t("adDetails.yes")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isImageViewerOpen && (
              <div className={styles.imageViewerOverlay} onClick={closeImageViewer}>
                <div
                  className={styles.imageViewerModal}
                  onClick={(event) => event.stopPropagation()}
                  onTouchStart={handleViewerTouchStart}
                  onTouchMove={handleViewerTouchMove}
                  onTouchEnd={handleViewerTouchEnd}
                  onTouchCancel={handleViewerTouchCancel}
                >
                  <button
                    type="button"
                    className={styles.imageViewerClose}
                    onClick={closeImageViewer}
                    aria-label={t("adDetails.close")}
                  >
                    ×
                  </button>
                  {photos.length > 1 && (
                    <button
                      type="button"
                      className={`${styles.imageViewerNav} ${styles.imageViewerPrev}`}
                      onClick={showPrevViewerImage}
                      aria-label={t("adDetails.previousMonth")}
                    >
                      <span className={styles.imageViewerNavIcon} aria-hidden="true">‹</span>
                    </button>
                  )}
                  <div
                    ref={viewerViewportRef}
                    className={styles.imageViewerViewport}
                  >
                    <div
                      className={styles.imageViewerTrack}
                      style={{
                        transform: `translate3d(${-viewerViewportWidth + viewerDragOffsetX}px, 0, 0)`,
                        opacity: viewerTrackOpacity,
                        transition: `transform ${viewerDragTransitionMs}ms ${viewerDragTransitionEasing}, opacity ${viewerTrackOpacityTransitionMs}ms ease`,
                      }}
                    >
                      <div className={styles.imageViewerSlide}>
                        <ShimmerImage
                          key={`viewer-prev-${viewerPrevImage}-${viewerPhotoIndex}`}
                          src={viewerPrevImage}
                          alt={t("adDetails.previewAlt", { index: ((viewerPhotoIndex - 1 + photos.length) % photos.length) + 1 })}
                          className={styles.imageViewerImage}
                          loading="eager"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                      <div className={styles.imageViewerSlide}>
                        <ShimmerImage
                          key={`viewer-current-${viewerImage}-${viewerPhotoIndex}`}
                          src={viewerImage}
                          alt={t("adDetails.previewAlt", { index: viewerPhotoIndex + 1 })}
                          className={styles.imageViewerImage}
                          loading="eager"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                      <div className={styles.imageViewerSlide}>
                        <ShimmerImage
                          key={`viewer-next-${viewerNextImage}-${viewerPhotoIndex}`}
                          src={viewerNextImage}
                          alt={t("adDetails.previewAlt", { index: ((viewerPhotoIndex + 1) % photos.length) + 1 })}
                          className={styles.imageViewerImage}
                          loading="eager"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                    </div>
                  </div>
                  {photos.length > 1 && (
                    <button
                      type="button"
                      className={`${styles.imageViewerNav} ${styles.imageViewerNext}`}
                      onClick={showNextViewerImage}
                      aria-label={t("adDetails.nextMonth")}
                    >
                      <span className={styles.imageViewerNavIcon} aria-hidden="true">›</span>
                    </button>
                  )}
                  <div className={styles.imageViewerCount}>
                    {viewerPhotoIndex + 1} / {photos.length}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
      {apiPendingCount > 0 && (
        <div className={`${loaderStyles.overlay} ${loaderStyles.active}`} role="status" aria-live="polite">
          <div className={loaderStyles.spinner} aria-hidden="true" />
          <p className={loaderStyles.label}>{t("loader.pageLoading")}</p>
        </div>
      )}
    </div>
  );
};

export default AdDetailsPage;
